import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API = '/api/ozon-finance';

// Auth fetch helper
const apiFetch = (path, opts = {}) => {
  const token = localStorage.getItem('ozon_token');
  return fetch(path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {})
    }
  });
};

// ── Yardımcı Fonksiyonlar ─────────────────────────────────────────────────────
const fmtNum = (n, d = 2) =>
  n == null ? '—' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

const fmtRub = (n) => (n == null ? '—' : `₽${fmtNum(n)}`);
const fmtUsd = (n) => (n == null ? '—' : `$${fmtNum(n)}`);
const fmtTry = (n) => (n == null ? '—' : `₺${fmtNum(n)}`);
const fmtPct = (n) => (n == null ? '—' : `%${fmtNum(n, 1)}`);

// TEK tarih fonksiyonu — ham ISO ekrana ASLA basılmaz (CLAUDE.md kural 6)
// Girdi: '2026-08-08', '2026-08-08T00:00:00.000Z', Date, null
const formatDate = (d) => {
  if (!d) return '—';
  let s;
  if (d instanceof Date) {
    if (isNaN(d)) return '—';
    const p = n => String(n).padStart(2, '0');
    s = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  } else {
    s = String(d).slice(0, 10);
  }
  const [y, m, day] = s.split('-');
  if (!y || !m || !day) return String(d);
  return `${day}.${m}.${y}`;
};

const MONTHS_TR = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const formatPeriodKey = (key) => {
  if (!key) return key;
  const [y, m, h] = key.split('-');
  const mon = MONTHS_TR[parseInt(m) - 1];
  const half = h === 'H1' ? `1–15 ${mon}` : `16–31 ${mon}`;
  return `${half} ${y}`;
};

// ── Badge ─────────────────────────────────────────────────────────────────────
const STATUS_MAP = {
  forecast:         { label: 'Tahmin',            color: '#6b7280' },
  in_period:        { label: 'Dönem Açık',         color: '#2563eb' },
  awaiting_payment: { label: 'Ödeme Bekleniyor',  color: '#d97706' },
  paid:             { label: 'Ödendi',             color: '#16a34a' },
  rolled_over:      { label: 'Devretti · Tahsil Edildi', color: '#0891b2' },
  pending:          { label: 'Beklemede',          color: '#6b7280' },
  // Sipariş statüleri (Sipariş Tarihleri sekmesi) — aksi halde ham İngilizce basılır
  awaiting_packaging:    { label: 'Paketleme Bekliyor', color: '#d97706' },
  awaiting_registration: { label: 'Kayıt Bekliyor',     color: '#d97706' },
  awaiting_deliver:      { label: 'Teslim Bekliyor',    color: '#0891b2' },
  ready_to_ship:         { label: 'Sevkiyata Hazır',    color: '#0891b2' },
  kargo_hazir:           { label: 'Kargo Hazır',        color: '#0891b2' },
  delivering:            { label: 'Kargoda',            color: '#2563eb' },
  delivered:             { label: 'Teslim Edildi',      color: '#16a34a' },
  cancelled:             { label: 'İptal',              color: '#dc2626' },
  arbitration:           { label: 'Anlaşmazlık',        color: '#dc2626' },
};

function Badge({ status }) {
  const s = STATUS_MAP[(status || '').trim()] || { label: status, color: '#888' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      background: s.color + '18', color: s.color, border: `1px solid ${s.color}44`
    }}>{s.label}</span>
  );
}

// Ozon accrual tip → Türkçe etiket
const TYPE_LABELS = {
  1:  'Acquiring (Tahsilat)',
  2:  'Geri Kargo',
  3:  'Marka Komisyonu',
  4:  'Marka Tanıtım',
  5:  'Marka Raf',
  6:  'İptal',
  9:  'Müşteri İadesi',
  19: 'Harici Tanıtım',
  21: 'Fulfillment',
  23: 'Site Reklamı',
  31: 'Müşteri Yaratma',
  36: 'Sipariş Rezervasyonu',
  40: 'Kısmi İade',
  41: 'Tıklama Başı Reklam',
  47: 'Değerlendirme Puanı',
  48: 'Premium Geri Ödeme (Puan)',
  49: 'Premium Geri Ödeme (Promosyon)',
  50: 'Premium E-posta Komisyonu',
  51: 'Premium Üyelik',
  52: 'Premium Abonelik',
  54: 'Promosyon',
  55: 'Push Kampanya',
  59: 'İade Lojistik',
  61: 'Değerlendirme Sabitleme',
  64: 'Yurt İçi Kargo',
  65: 'Kolay İade',
  66: 'Uluslararası Acentelik',
  67: 'Uluslararası Kargo',
  68: 'RFBS Hizmet Ücreti',
  69: 'Satış Komisyonu',
  70: 'Satış Değerlendirmesi',
  74: 'Yıldız Üyelik (Pazarlama)',
  75: 'Şablon',
  76: 'Stok Sigortası',
  80: 'Video Kapak',
  81: 'Hacim Ödülü',
};
function typeLabel(id, name) { return TYPE_LABELS[parseInt(id)] || name || `Tip ${id}`; }

// ── Ödemeler Tab ─────────────────────────────────────────────────────────────
function PayoutsTab({ onSelectPayout, focusPayoutId }) {
  const [payouts, setPayouts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    apiFetch(`${API}/payouts?year=all`).then(r => r.json()).then(d => {
      setPayouts(Array.isArray(d) ? d : d.payouts || []);
      setLoading(false);
    });
  }, []);

  const loadDetail = useCallback((id) => {
    setDetail(null);
    setSelectedId(id);
    setEditing(false);
    apiFetch(`${API}/payouts/${id}`).then(r => r.json()).then(d => {
      setDetail(d);
      setEditForm({ amount_try: d.amount_try || '', payout_date: d.payout_date ? d.payout_date.slice(0,10) : '', status: d.status });
    });
  }, []);

  // Siparisler sekmesindeki Donem hucresinden gelindiyse o donemi otomatik ac
  useEffect(() => {
    if (focusPayoutId) loadDetail(focusPayoutId);
  }, [focusPayoutId, loadDetail]);

  const saveEdit = async () => {
    const res = await apiFetch(`${API}/payouts/${selectedId}`, {
      method: 'PATCH', body: JSON.stringify(editForm)
    });
    const updated = await res.json();
    setDetail(d => ({ ...d, ...updated }));
    setPayouts(prev => prev.map(p => p.id === selectedId ? { ...p, ...updated } : p));
    setEditing(false);
  };

  if (loading) return <div style={styles.loading}>Yükleniyor…</div>;

  return (
    <div style={{ display: 'flex', minHeight: 500 }}>
      {/* Sol: Dönem Listesi */}
      <div style={{ width: 520, borderRight: '1px solid #e5e7eb', overflowY: 'auto', flexShrink: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb', position: 'sticky', top: 0 }}>
              <th style={th}>Dönem</th>
              <th style={{ ...th, textAlign: 'right' }}>Kapanış ₽</th>
              <th style={{ ...th, textAlign: 'right' }}>USD</th>
              <th style={{ ...th, textAlign: 'right' }}>TL</th>
              <th style={{ ...th, minWidth: 120 }}>Durum</th>
            </tr>
          </thead>
          <tbody>
            {payouts.map(p => (
              <tr key={p.id} onClick={() => loadDetail(p.id)}
                style={{ cursor: 'pointer', background: selectedId === p.id ? '#eff6ff' : 'white', borderBottom: '1px solid #f0f0f0' }}>
                <td style={td}>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{formatPeriodKey(p.period_key)}</div>
                  {p.payout_date && <div style={{ fontSize: 11, color: '#888' }}>Ödeme: {formatDate(p.payout_date)}</div>}
                  {p.order_count > 0 && <div style={{ fontSize: 11, color: '#aaa' }}>{p.order_count} sipariş</div>}
                </td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{fmtRub(p.closing_balance_rub)}</td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>{fmtUsd(p.amount_usd)}</td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontSize: 12, color: p.amount_try ? '#111' : '#ccc' }}>{p.amount_try ? fmtTry(p.amount_try) : '—'}</td>
                {/* ⚠️ ozon_payouts.status bayat: 08-H1 "Ödeme Bekleniyor" diyordu, oysa
                    kapanışı 19.08'de transfer edilmişti. Rozet artık ÖLÇÜLEN para
                    hareketinden geliyor; hareket yoksa eski statüye düşülür. */}
                <td style={td}>
                  {parseFloat(p.kapanis_hareketi_rub || 0) > 0.01 ? (
                    <span title={`Kapanış ${fmtRub(p.closing_balance_rub)} → ${formatDate(p.kapanis_hareketi_tarihi)} tarihinde ${fmtRub(p.kapanis_hareketi_rub)} çıktı`}
                      style={{ fontSize: 11, background: '#dcfce7', color: '#166534', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                      Kapatıldı · {formatDate(p.kapanis_hareketi_tarihi)}
                    </span>
                  ) : <Badge status={p.status} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sağ: Detay Paneli */}
      <div style={{ flex: 1, padding: 24, overflowY: 'auto', minWidth: 0 }}>
        {!selectedId && <div style={{ color: '#aaa', textAlign: 'center', marginTop: 80 }}>← Dönem seçin</div>}
        {selectedId && !detail && <div style={styles.loading}>Yükleniyor…</div>}
        {detail && (
          <PayoutDetail
            detail={detail}
            editing={editing}
            editForm={editForm}
            setEditForm={setEditForm}
            onEdit={() => setEditing(true)}
            onCancel={() => setEditing(false)}
            onSave={saveEdit}
            onSeeOrders={() => onSelectPayout(detail.id)}
          />
        )}
      </div>
    </div>
  );
}

// Settlement ledger tip → Türkçe etiket
const LEDGER_LABELS = {
  realization:    'Realization Raporu (Satış Geliri)',
  points_bonus:   'Puan Primi',
  fx_revaluation: 'Kur Farkı (Gerçekleşmemiş)',
  act:            'Акт — Hizmet Bedeli',
  service_act:    'Акт — Hizmet Bedeli',
  product_compensation: 'Ürün Tazminatı',
  opening:        'Açılış Bakiyesi',
  closing:        'Kapanış Bakiyesi',
  other:          'Diğer (tanınmayan belge)',
  reinvoice:      'Yeniden Faturalama (Nakliye/Acquiring)',
  stars_act:      'Yıldız Ürünler Aktı',
  agency_report:  'Acente Raporu',
  payment:        'Ödeme (Banka Transferi)',
};

function PayoutDetail({ detail, editing, editForm, setEditForm, onEdit, onCancel, onSave, onSeeOrders }) {
  const ledger  = Array.isArray(detail.ledger) ? detail.ledger : [];
  // Tahakkuk kalemleri (ozon_accrual_v2). Settlement raporu yoksa ekranda bu gösterilir.
  const breakdown = Array.isArray(detail.breakdown) ? detail.breakdown : [];
  const kopru = detail.kopru || null;
  // Kapanışın akıbeti: SONRAKİ dönemde çıkan ödeme/transfer. Yoksa bakiye devrediyor.
  const ak = detail.kapanis_akibeti;
  const akibet = ak ? { ...ak, tarih: detail.kapanis_hareketi_tarihi } : null;
  const tahakkukUsd = breakdown.reduce((a, b) => a + parseFloat(b.total_usd || 0), 0);

  // Açılış/kapanış sağlıdosu HAREKET DEĞİLDİR — gelir/gider listelerine girmemeli,
  // yoksa kapanış bakiyesi gelir gibi sayılıp toplamı ikiye katlar.
  const openingRow = ledger.find(r => r.line_type === 'opening');
  const closingRow = ledger.find(r => r.line_type === 'closing');
  const hareket = ledger.filter(r => r.line_type !== 'opening' && r.line_type !== 'closing');

  // Sağlıdo ledger'da varsa onu kullan (rapor gerçeği), yoksa payout kaydına düş
  const opening = openingRow ? parseFloat(openingRow.net_rub) : parseFloat(detail.opening_balance_rub || 0);
  const closing = closingRow ? parseFloat(closingRow.net_rub) : parseFloat(detail.closing_balance_rub || 0);

  // Hareketleri GELİR / GİDER / ÖDEME olarak ayır
  const gelirRows   = hareket.filter(r => parseFloat(r.net_rub) > 0.01 && r.line_type !== 'payment');
  const giderRows   = hareket.filter(r => parseFloat(r.net_rub) < -0.01 && r.line_type !== 'payment');
  const odemeRows   = hareket.filter(r => r.line_type === 'payment');

  const totalGelir  = gelirRows.reduce((s, r) => s + parseFloat(r.net_rub), 0);
  const totalGider  = giderRows.reduce((s, r) => s + Math.abs(parseFloat(r.net_rub)), 0);
  const totalOdeme  = odemeRows.reduce((s, r) => s + Math.abs(parseFloat(r.net_rub)), 0);

  // Mutabakat: opening + gelir - gider - odeme = closing
  const hesaplanan = opening + totalGelir - totalGider - totalOdeme;
  const sapma      = Math.abs(hesaplanan - closing);
  const mutabakatOk = sapma < 1;

  const implRate = detail.amount_try && detail.amount_usd
    ? (parseFloat(detail.amount_try) / parseFloat(detail.amount_usd)).toFixed(2)
    : null;

  return (
    <div>
      {/* Başlık */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{formatPeriodKey(detail.period_key)}</h2>
          <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
            {akibet ? (
              <span style={{ fontSize: 11, background: '#dcfce7', color: '#166534', padding: '2px 7px', borderRadius: 4 }}>
                Kapatıldı · {akibet.tip === 'transfer' ? 'transfer' : 'ödeme'} {formatDate(akibet.tarih)}
              </span>
            ) : <Badge status={detail.status} />}
            {detail.period_ym && <span style={{ fontSize: 11, color: '#888' }}>Settlement: {detail.period_ym}</span>}
            {detail.doc_number && <span style={{ fontSize: 11, color: '#888' }}>Справка №{detail.doc_number}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!editing
            ? <><button onClick={onEdit} style={styles.btnSec}>Düzenle</button>
                <button onClick={onSeeOrders} style={styles.btnPri}>{detail.order_count} siparişi gör →</button></>
            : <><button onClick={onCancel} style={styles.btnSec}>İptal</button>
                <button onClick={onSave} style={styles.btnPri}>Kaydet</button></>
          }
        </div>
      </div>

      {/* Kur satırı */}
      {detail.cbr_rate && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '7px 14px', marginBottom: 14, fontSize: 12, color: '#0369a1' }}>
          {detail.usd_kaynak === 'doviz_belgesi' ? (
            <><strong>Ödeme tutarı:</strong> döviz ödeme belgesinden <strong>{fmtUsd(detail.amount_usd)}</strong>
              <span style={{ marginLeft: 8, color: '#64748b' }}>(kapanış {fmtRub(detail.closing_balance_rub)} · CBR {fmtNum(detail.cbr_rate, 4)} ₽/$ {formatDate(detail.cbr_rate_date)})</span></>
          ) : (
            <><strong>CBR Kuru:</strong> {fmtRub(detail.closing_balance_rub)} ÷ {fmtNum(detail.cbr_rate, 4)} ₽/$ = <strong>{fmtUsd(detail.amount_usd)}</strong>
              <span style={{ marginLeft: 8, color: '#64748b' }}>({formatDate(detail.cbr_rate_date)}) · belge henüz yok, hesaplanmış değer</span></>
          )}
          {implRate && <span style={{ marginLeft: 16 }}><strong>USD/TL:</strong> ₺{implRate}</span>}
        </div>
      )}

      {/* Özet kartlar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
        <SummaryCard label="Kapanış Bakiyesi" value={fmtRub(detail.closing_balance_rub)}
          sub={`Açılış: ${fmtRub(detail.opening_balance_rub)}${detail.tutar_kaynak === 'cash_flow' ? ' · nakit akış raporu' : ' · eski kayıt'}`} />
        {/* ⚠️ Ödenmemiş dönemde bu rakam "bu dönemin ödemesi" DEĞİLDİR: kapanış bakiyesi
            önceki dönemden devreden bakiyeyi de içerir. 09-H2'de 35.001,30 $ görünüyordu
            ama dönemin kendi tahakkuku 15.588,85 $; farkı 09-H1'den devreden bakiye.
            (kullanıcı 23.09.2026'da yakaladı) */}
        <SummaryCard label={akibet ? 'Ödeme (USD)' : 'Kapanış bakiyesi ($)'}
          value={fmtUsd(detail.amount_usd)} accent="#2563eb"
          sub={akibet
            ? (detail.usd_kaynak === 'doviz_belgesi' ? 'Döviz ödeme belgesi' : 'Hesaplanmış (belge yok)')
            : `Henüz ödenmedi · bu dönemin tahakkuku ${fmtUsd(tahakkukUsd)}, kalanı devreden bakiye`} />
        {/* Dönem İÇİNDE çıkan para. Hangi dönemin borcunu kapattığı BAŞKA bir sorudur:
            11.08'deki ödeme 08-H1'e düşer ama 07-H2'nin kapanışını kapatır. O yüzden
            burada "bu dönem ödendi" DENMİYOR — sadece para hareketi gösteriliyor. */}
        {detail.donem_ici_odeme_rub != null && (
          <SummaryCard label="Dönem içinde çıkan ödeme"
            value={fmtRub(Math.abs(parseFloat(detail.donem_ici_odeme_rub)))}
            sub={`${formatDate(detail.donem_ici_odeme_tarihi)} · önceki dönemin kapanışını kapatır`} />
        )}
        <SummaryCard
          label="Ödeme (TL)"
          value={editing
            ? <input value={editForm.amount_try} onChange={e => setEditForm(f => ({ ...f, amount_try: e.target.value }))}
                style={styles.input} placeholder="₺ giriniz" />
            : fmtTry(detail.amount_try)
          }
          sub={editing
            ? <input type="date" value={editForm.payout_date} onChange={e => setEditForm(f => ({ ...f, payout_date: e.target.value }))}
                style={{ ...styles.input, marginTop: 4 }} />
            : detail.payout_date ? `Ödeme: ${formatDate(detail.payout_date)}` : 'Tarih girilmedi'
          }
        />
      </div>

      {/* Settlement Kırılımı */}
      {/* ── Bakiye köprüsü ──────────────────────────────────────────────────────
          "Yukarıdaki rakamla aşağısı tutmuyor" sorusunun cevabı (kullanıcı, 23.09.2026):
          kapanış bakiyesi yalnız tahakkuktan oluşmaz. Aynı denklem bakiye kapısında
          sınanıyor; burada gösterilen hesabın ta kendisi. Kapanan fark yoksa
          DENKLEŞTİRME SATIRI EKLENMEZ — kalan "açıklanmayan" olarak yazılır. */}
      {kopru && kopru.kapanis != null && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
            Kapanış bakiyesi nasıl oluştu?
            <Ipucu metin="Kapanış = açılış + dönemin tahakkuku + CPO kayması + kur farkı + para çıkışı. Tahakkuk listesi tek başına kapanışı vermez; bu köprü aradaki her kalemi gösterir." />
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <tbody>
              {[
                ['Açılış bakiyesi', kopru.girdiler?.acilis?.deger, 'nakit akış raporu'],
                ['Dönemin tahakkuku', kopru.girdiler?.net?.deger, 'tahakkuk kayıtları (aşağıdaki liste)'],
                ['CPO kayması', kopru.girdiler?.cpo_kayma?.deger, 'dönem sınırına takılan tip 54'],
                ['Kur farkı', kopru.girdiler?.kur_farki?.deger, 'mutabakat raporu'],
                ['Ödeme (para çıkışı)', kopru.girdiler?.odeme?.deger, 'günlük tahakkuk tip 11'],
                ['Transfer', kopru.girdiler?.transfer?.deger, 'mutabakat raporu transfer satırı'],
              ].filter(([, v]) => v != null && Math.abs(parseFloat(v)) > 0.005).map(([ad, v, kaynak]) => (
                <tr key={ad} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ ...td, width: '40%' }}>{ad}
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>{kaynak}</div></td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace',
                               color: parseFloat(v) < 0 ? '#dc2626' : '#111' }}>{fmtRub(v)}</td>
                </tr>
              ))}
              {kopru.aciklanmayan != null && Math.abs(kopru.aciklanmayan) > 0.01 && (
                <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#fffbeb' }}>
                  <td style={{ ...td, color: '#92400e' }}>Açıklanmayan
                    <div style={{ fontSize: 10, color: '#b45309' }}>
                      {kopru.aciklama || 'kaynağı gözlenmemiş fark'}
                    </div></td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#92400e' }}>
                    {fmtRub(kopru.aciklanmayan)}
                  </td>
                </tr>
              )}
              <tr style={{ fontWeight: 700, background: '#f0f4ff' }}>
                <td style={td}>Kapanış bakiyesi</td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtRub(kopru.kapanis)}</td>
              </tr>
            </tbody>
          </table>
          {akibet && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#166534', background: '#f0fdf4',
                          border: '1px solid #bbf7d0', borderRadius: 6, padding: '6px 10px' }}>
              Bu kapanış <strong>{formatDate(akibet.tarih)}</strong> tarihinde
              {akibet.tip === 'transfer' ? ' transfer' : ' ödeme'} olarak çıktı: {fmtRub(akibet.tutar)}
              {Math.abs(akibet.fark) > 0.01 && (
                <> · aradaki {fmtRub(akibet.fark)}{akibet.kur_farki_aciklar
                  ? ' dönem sonu kur farkının ters kaydı (büyüklüğü birebir tutuyor)'
                  : ' fark kaynağı gözlenmedi'}</>
              )}
            </div>
          )}
          {!akibet && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#92400e' }}>
              Bu bakiye henüz ödenmedi; sonraki döneme devrediyor.
            </div>
          )}
        </div>
      )}

      {ledger.length === 0 ? (
        // ⚠️ 23.09.2026: burada eskiden yalnız "Settlement Report yüklenmedi" yazıyordu ve
        // ekran bomboş kalıyordu. Settlement raporu AYLIK yayımlanır (settlement_ledger
        // period_ym ile anahtarlı), o yüzden içinde bulunulan ay hep boş görünüyordu.
        // Tahakkuk kırılımı ise canlı: ozon_accrual_v2. Rapor gelene kadar onu gösteriyoruz.
        // İkisi AYNI ŞEY DEĞİL: ledger dönemin tüm hareketlerini (ödeme, kur farkı, prim
        // dahil) içerir; buradaki liste yalnız tahakkuk kalemleridir — bu yüzden bu
        // görünümde açılış+hareket=kapanış mutabakatı YAPILMAZ, sadece kalemler listelenir.
        <div>
          <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a',
                        borderRadius: 8, color: '#92400e', fontSize: 12, marginBottom: 10 }}>
            Settlement Report henüz yüklenmedi (aylık yayımlanır) — aşağıdaki liste
            <strong> tahakkuk kalemleridir</strong> (kaynak: tahakkuk kayıtları). Ödeme,
            kur farkı ve prim satırları rapor gelince eklenir; bu yüzden burada
            açılış/kapanış mutabakatı gösterilmez.
          </div>
          {breakdown.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              Bu dönem için tahakkuk kaydı da yok.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>   {/* detay paneli dar; tutar sütunları taşmasın */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 460 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={th}>Kalem</th>
                  <th style={th}>Kategori</th>
                  <th style={{ ...th, textAlign: 'right' }}>Gönderi</th>
                  <th style={{ ...th, textAlign: 'right' }}>Tutar ₽</th>
                  <th style={{ ...th, textAlign: 'right' }}>Tutar $</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((b, i) => {
                  const rub = parseFloat(b.total_rub || 0);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={td} title={b.name_ru || ''}>{tipTr(b.name_ru) || b.name_en || '—'}</td>
                      <td style={{ ...td, color: '#888' }}>{b.category || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>
                        {parseInt(b.order_count || 0) || '—'}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace',
                                   color: rub < 0 ? '#dc2626' : '#16a34a' }}>{fmtRub(rub)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#6b7280' }}>
                        {b.total_usd != null ? fmtUsd(b.total_usd) : '—'}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700, background: '#f0f4ff' }}>
                  <td colSpan={3} style={{ ...td, textAlign: 'right' }}>TAHAKKUK NET</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>
                    {fmtRub(breakdown.reduce((s2, b) => s2 + parseFloat(b.total_rub || 0), 0))}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#6b7280' }}>
                    {fmtUsd(breakdown.reduce((s2, b) => s2 + parseFloat(b.total_usd || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
            {/* GELİR */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#16a34a', marginBottom: 8 }}>GELİR</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {gelirRows.map((r, i) => (
                    <BreakRow key={i} label={LEDGER_LABELS[r.line_type] || r.name_ru} rub={parseFloat(r.net_rub)} color="#16a34a" sub={[r.doc_number, formatDate(r.doc_date)].filter(Boolean).join(' • ')} />
                  ))}
                </tbody>
              </table>
              <div style={{ borderTop: '1px solid #bbf7d0', marginTop: 6, paddingTop: 6, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#16a34a', fontSize: 13 }}>
                {fmtRub(totalGelir)}
              </div>
            </div>

            {/* GİDER */}
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', marginBottom: 8 }}>GİDER (Hizmet Bedelleri)</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {giderRows.map((r, i) => {
                    const abs = Math.abs(parseFloat(r.net_rub));
                    const pct = totalGider > 0 ? abs / totalGider * 100 : 0;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #fee2e2' }}>
                        <td style={{ padding: '4px 4px 4px 0', color: '#555' }}>
                          <div style={{ fontSize: 11 }}>{LEDGER_LABELS[r.line_type] || r.name_ru}</div>
                          <div style={{ fontSize: 10, color: '#999' }}>{[r.doc_number, formatDate(r.doc_date)].filter(Boolean).join(' • ')}</div>
                          <div style={{ background: '#fee2e2', borderRadius: 2, height: 3, marginTop: 2 }}>
                            <div style={{ background: '#dc2626', width: `${pct}%`, height: '100%', borderRadius: 2 }} />
                          </div>
                        </td>
                        <td style={{ padding: '4px 0', textAlign: 'right', fontFamily: 'monospace', color: '#dc2626', whiteSpace: 'nowrap', fontSize: 11 }}>
                          {fmtRub(parseFloat(r.net_rub))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ borderTop: '1px solid #fecaca', marginTop: 6, paddingTop: 6, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#dc2626', fontSize: 13 }}>
                −{fmtRub(totalGider)}
              </div>
            </div>
          </div>

          {/* Ödemeler */}
          {odemeRows.length > 0 && (
            <div style={{ background: '#f8faff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 14px', marginBottom: 8, fontSize: 12 }}>
              <span style={{ fontWeight: 600, color: '#3730a3' }}>Bu Ay Yapılan Ödemeler: </span>
              {odemeRows.map((r, i) => (
                <span key={i} style={{ marginLeft: 8, fontFamily: 'monospace' }}>
                  {fmtRub(parseFloat(r.net_rub))} <span style={{ color: '#888', fontSize: 11 }}>({r.doc_number} / {formatDate(r.doc_date)})</span>
                </span>
              ))}
              <span style={{ marginLeft: 16, fontFamily: 'monospace', fontWeight: 700, color: '#dc2626' }}>
                Toplam: −{fmtRub(totalOdeme)}
              </span>
            </div>
          )}

          {/* Net */}
          <div style={{ background: '#f8faff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>NET (Kapanış Bakiyesi)</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16 }}>{fmtRub(closing)} = {fmtUsd(detail.amount_usd)}</span>
          </div>

          {/* Mutabakat */}
          <div style={{ marginTop: 8, padding: '5px 12px', borderRadius: 6, fontSize: 11,
            background: mutabakatOk ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${mutabakatOk ? '#bbf7d0' : '#fecaca'}`,
            color: mutabakatOk ? '#15803d' : '#dc2626' }}>
            {mutabakatOk
              ? `✅ Mutabakat: Açılış (${fmtRub(opening)}) + Gelir (${fmtRub(totalGelir)}) − Gider (${fmtRub(totalGider)}) − Ödeme (${fmtRub(totalOdeme)}) = ${fmtRub(hesaplanan)}`
              : `❌ Mutabakat hatası: Hesaplanan ${fmtRub(hesaplanan)} ≠ Kapanış ${fmtRub(closing)} (fark: ${fmtRub(sapma)})`
            }
          </div>
        </>
      )}
    </div>
  );
}

function BreakRow({ label, rub, color, sub }) {
  return (
    <tr style={{ borderBottom: '1px solid #d1fae5' }}>
      <td style={{ padding: '4px 0', color: '#555' }}>
        <div style={{ fontSize: 12 }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: '#999' }}>{sub}</div>}
      </td>
      <td style={{ padding: '4px 0', textAlign: 'right', fontFamily: 'monospace', color }}>{fmtRub(rub)}</td>
    </tr>
  );
}

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: accent ? accent + '11' : '#f9fafb', border: `1px solid ${accent || '#e5e7eb'}`, borderRadius: 8, padding: '10px 14px' }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: accent || '#111', fontFamily: 'monospace' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// Tarih aralığı filtresinin uygulanabileceği alanlar (backend ile AYNI anahtarlar).
const TARIH_SUTUNLARI = [
  { key: 'delivered_at',    label: 'Teslim Trh' },
  { key: 'siparis_tarihi',  label: 'Sipariş Trh' },
  { key: 'sevk_tarihi',     label: 'Sevk Trh' },
  { key: 'kargo_tarihi',    label: 'Kargo Trh' },
  { key: 'teslim_plan',     label: 'Teslim (Plan)' },
  { key: 'teslim_gercek',   label: 'Teslim (Gerçek)' },
  { key: 'tahakkuk_tarihi', label: 'Tahakkuk Trh' },
];
// "Tarihler" düğmesiyle açılan ek sütunlar. Sipariş/Teslim zaten sabit sütun.
// Teslim (Gerçek): statünün 'delivered'a geçtiği an — sync yalnız GÖRDÜĞÜ geçişi
// kaydettiği için eski siparişlerde boş kalır; plan tarihiyle karıştırılmamalı.
const EK_TARIHLER = [
  { key: 'sevk_tarihi',     label: 'Sevk' },
  { key: 'kargo_tarihi',    label: 'Kargo' },
  { key: 'teslim_plan',     label: 'Teslim (Plan)' },
  { key: 'teslim_gercek',   label: 'Teslim (Gerçek)' },
  { key: 'tahakkuk_tarihi', label: 'Tahakkuk' },
];

// ── Siparişler Tab ────────────────────────────────────────────────────────────
function OrdersTab({ initialPayoutId, payouts, onGoToPayout }) {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [accruals, setAccruals] = useState({});
  const [page, setPage] = useState(1);
  const [currency, setCurrency] = useState('RUB');
  const PAGE_SIZE = 50;

  // ⚠️ 23.09.2026: "Sipariş Tarihleri" sekmesi buraya katıldı.
  //   kapsam='finans' → tahakkuku olan 13.827 sipariş (bu ekranın eski davranışı)
  //   kapsam='tumu'   → mağazadaki 17.319 siparişin tamamı; 3.612'si henüz yolda/iptal
  //                     olduğu için finans sütunları boş kalır (sıfır DEĞİL, boş).
  const [kapsam, setKapsam]   = useState('finans');
  const [tarihler, setTarihler] = useState(false);   // tarih sütunları açık mı
  const [finanssiz, setFinanssiz] = useState(0);
  const [filters, setFilters] = useState({
    q: '', status: '', payout_id: initialPayoutId || '',
    from: '', to: '', sort: 'delivered_at', dir: 'desc', tarih_alani: 'delivered_at'
  });

  useEffect(() => {
    if (initialPayoutId) setFilters(f => ({ ...f, payout_id: String(initialPayoutId) }));
  }, [initialPayoutId]);

  const setFilter = (key, val) => { setPage(1); setFilters(f => ({ ...f, [key]: val })); };

  // Tarih başlığına tıklayınca o alana göre sırala; aynı başlığa tekrar tıklama yönü çevirir.
  // Sıralama SUNUCUDA yapılır (sayfa başına 50 kayıt geliyor) — yoksa yalnız görünen
  // sayfa sıralanır ve kullanıcı yanlışlıkla "en eski sipariş" sanır.
  const siralaBaslik = (key) => {
    setPage(1);
    setFilters(f => f.sort === key
      ? { ...f, dir: f.dir === 'asc' ? 'desc' : 'asc' }
      : { ...f, sort: key, dir: 'desc' });
  };
  const BaslikTarih = ({ sortKey, children }) => (
    <th onClick={() => siralaBaslik(sortKey)} title="Sıralamak için tıklayın"
        style={{ ...th, cursor: 'pointer', whiteSpace: 'nowrap',
                 color: filters.sort === sortKey ? '#2563eb' : '#555' }}>
      {children}{filters.sort === sortKey ? (filters.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const loadOrders = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: PAGE_SIZE, sort: filters.sort, dir: filters.dir,
                                        kapsam, tarih_alani: filters.tarih_alani });
    if (filters.q)         params.set('q', filters.q);
    if (filters.status)    params.set('status', filters.status);
    if (filters.payout_id) params.set('payout_id', filters.payout_id);
    if (filters.from)      params.set('from', filters.from);
    if (filters.to)        params.set('to', filters.to);
    apiFetch(`${API}/orders?${params}`).then(r => r.json()).then(d => {
      setOrders(d.orders || []);
      setTotal(d.total || 0);
      setFinanssiz(d.summary?.finanssiz || 0);
      setLoading(false);
    });
  }, [filters, page, kapsam]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const toggleExpand = async (pn) => {
    if (expanded === pn) { setExpanded(null); return; }
    setExpanded(pn);
    if (!accruals[pn]) {
      const d = await apiFetch(`${API}/orders/${pn}/accruals`).then(r => r.json());
      setAccruals(prev => ({ ...prev, [pn]: Array.isArray(d) ? d : (d.accruals || []) }));
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const useUsd = currency === 'USD';

  // CSV: GÖRÜNEN sayfa değil, filtreye uyan kümenin TAMAMI — sunucudan tek seferde
  // hafif modda çekilir (ürün JSON'u taşınmaz, yalnız ilk ürünün adı).
  // Tutarlar ham sayı olarak yazılır (Excel'de hesaplanabilsin); ayraç ';', tarih gg.aa.yyyy.
  const [csvDurum, setCsvDurum] = useState('');
  const csvIndir = async () => {
    setCsvDurum('Hazırlanıyor…');
    try {
      const params = new URLSearchParams({ page: 1, limit: 100000, sort: filters.sort,
        dir: filters.dir, kapsam, tarih_alani: filters.tarih_alani, hafif: '1' });
      if (filters.q)         params.set('q', filters.q);
      if (filters.status)    params.set('status', filters.status);
      if (filters.payout_id) params.set('payout_id', filters.payout_id);
      if (filters.from)      params.set('from', filters.from);
      if (filters.to)        params.set('to', filters.to);
      const d = await apiFetch(`${API}/orders?${params}`).then(r => r.json());
      const satirlar = d.orders || [];

      const sayi = v => (v == null || v === '' ? '' : String(v).replace('.', ','));
      const tarih = v => { const t = formatDate(v); return t === '—' ? '' : t; };
      const kacir = v => {
        const t = v == null ? '' : String(v);
        return /[";\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
      };
      const bas = ['Gönderi No', 'Sipariş No', 'Sipariş Trh', 'Teslim Trh',
        'Sevk Trh', 'Kargo Trh', 'Teslim (Plan)', 'Teslim (Gerçek)', 'Tahakkuk Trh',
        'Ürün', 'Adet', 'Gelir ₽', 'Gelir $', 'Komisyon ₽', 'Kargo ₽', 'Acentelik ₽',
        'Acquiring ₽', 'Reklam ₽', 'İade ₽', 'Diğer ₽', 'Net ₽', 'Net $', 'Marj %',
        'Kur ₽/$', 'Dönem', 'Durum'];
      const csv = 'sep=;\n' + [bas.map(kacir).join(';'), ...satirlar.map(o => {
        const n = v => parseFloat(v || 0);
        // Tahakkuku olmayan siparişte finans alanları BOŞ kalır — sıfır yazmak yanlış olur.
        const finansVar = o.net_rub != null;
        const diger = finansVar
          ? n(o.subsidy_rub) + n(o.logistics_rub) + n(o.penalty_rub) + n(o.other_rub) : null;
        return [
          o.posting_number, o.order_number || '',
          tarih(o.order_date || o.siparis_tarihi_ham), tarih(o.delivered_at),
          tarih(o.sevk_tarihi), tarih(o.kargo_tarihi), tarih(o.teslim_plan),
          tarih(o.teslim_gercek), tarih(o.tahakkuk_tarihi),
          o.ilk_urun || '', o.product_count != null ? o.product_count : '',
          sayi(o.revenue_rub), sayi(o.revenue_usd), sayi(o.commission_rub),
          sayi(o.shipping_rub), sayi(o.agency_rub), sayi(o.acquiring_rub),
          sayi(o.marketing_rub), sayi(o.returns_rub), diger == null ? '' : sayi(diger),
          sayi(o.net_rub), sayi(o.net_usd), sayi(o.margin_pct), sayi(o.fx_rate),
          o.period_key ? formatPeriodKey(o.period_key) : '',
          (STATUS_MAP[(o.payment_status || o.siparis_statu || '').trim()] || {}).label
            || o.payment_status || o.siparis_statu || '',
        ].map(kacir).join(';');
      })].join('\n');

      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `siparisler-${kapsam}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setCsvDurum(`${satirlar.length.toLocaleString('tr-TR')} satır indirildi`);
      setTimeout(() => setCsvDurum(''), 4000);
    } catch (e) {
      setCsvDurum('Hata: ' + e.message);   // sessizce yutma
    }
  };

  return (
    <div>
      {/* Filtreler */}
      <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Gönderi / sipariş ara…" value={filters.q}
          onChange={e => setFilter('q', e.target.value)} style={{ ...styles.input, width: 200 }} />
        <select value={filters.payout_id} onChange={e => setFilter('payout_id', e.target.value)} style={styles.input}>
          <option value="">Tüm dönemler</option>
          {(payouts || []).map(p => <option key={p.id} value={p.id}>{formatPeriodKey(p.period_key)}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)} style={styles.input}>
          <option value="">Tüm durumlar</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filters.tarih_alani} onChange={e => setFilter('tarih_alani', e.target.value)}
          style={styles.input} title="Tarih aralığı hangi tarihe uygulanacak">
          {TARIH_SUTUNLARI.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <input type="date" value={filters.from} onChange={e => setFilter('from', e.target.value)} style={styles.input} />
        <input type="date" value={filters.to} onChange={e => setFilter('to', e.target.value)} style={styles.input} />
        <button onClick={() => setTarihler(v => !v)} style={{ ...styles.btnSec,
          background: tarihler ? '#2563eb' : 'white', color: tarihler ? 'white' : '#374151' }}
          title="Sevk / kargo / teslim / tahakkuk tarihi sütunlarını göster">
          Tarihler
        </button>
        <select value={`${filters.sort}_${filters.dir}`}
          onChange={e => {
            // Anahtarda alt çizgi var (siparis_tarihi_desc) — SON alt çizgiden böl,
            // yoksa sort 'siparis' olur ve sunucu varsayılana düşer.
            const v = e.target.value, i = v.lastIndexOf('_');
            setPage(1); setFilters(f => ({ ...f, sort: v.slice(0, i), dir: v.slice(i + 1) }));
          }}
          style={styles.input}>
          <option value="delivered_at_desc">Teslim ↓</option>
          <option value="delivered_at_asc">Teslim ↑</option>
          <option value="net_rub_desc">Net ₽ ↓</option>
          <option value="net_rub_asc">Net ₽ ↑</option>
          <option value="siparis_tarihi_desc">Sipariş ↓</option>
          <option value="siparis_tarihi_asc">Sipariş ↑</option>
          <option value="tahakkuk_tarihi_desc">Tahakkuk ↓</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {['RUB','USD'].map(c => (
            <button key={c} onClick={() => setCurrency(c)}
              style={{ ...styles.btnSec, background: currency === c ? '#2563eb' : 'white', color: currency === c ? 'white' : '#374151' }}>
              {c === 'RUB' ? '₽' : '$'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4 }} title="Finans: tahakkuku olan siparişler · Tümü: yoldaki ve iptal siparişler de dahil">
          {[['finans','Finans'],['tumu','Tümü']].map(([k,l]) => (
            <button key={k} onClick={() => { setPage(1); setKapsam(k); }}
              style={{ ...styles.btnSec, background: kapsam === k ? '#0f766e' : 'white',
                       color: kapsam === k ? 'white' : '#374151' }}>{l}</button>
          ))}
        </div>
        <button onClick={csvIndir} disabled={csvDurum === 'Hazırlanıyor…'}
          style={{ ...styles.btnPri, background: '#16a34a' }}
          title="Filtreye uyan TÜM siparişler (görünen sayfa değil)">
          ⭳ CSV
        </button>
        {csvDurum && <span style={{ fontSize: 11, color: csvDurum.startsWith('Hata') ? '#dc2626' : '#6b7280' }}>{csvDurum}</span>}
        <div style={{ color: '#888', fontSize: 12 }}>
          {total.toLocaleString('tr-TR')} sipariş
          {finanssiz > 0 && <span style={{ color: '#b45309' }}> · {finanssiz.toLocaleString('tr-TR')} tahakkuksuz</span>}
        </div>
      </div>

      {loading && <div style={styles.loading}>Yükleniyor…</div>}
      {!loading && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={th}>Gönderi No</th>
                <BaslikTarih sortKey="siparis_tarihi">Sipariş Trh</BaslikTarih>
                <BaslikTarih sortKey="delivered_at">Teslim Trh</BaslikTarih>
                {tarihler && EK_TARIHLER.map(c => (
                  <BaslikTarih key={c.key} sortKey={c.key}>{c.label}</BaslikTarih>
                ))}
                <th style={th}>Ürün</th>
                <th style={{ ...th, textAlign: 'right' }}>Adet</th>
                <th style={{ ...th, textAlign: 'right' }}>Gelir</th>
                <th style={{ ...th, textAlign: 'right' }}>Komisyon</th>
                <th style={{ ...th, textAlign: 'right' }}>Kargo</th>
                <th style={{ ...th, textAlign: 'right' }}>Acentelik</th>
                <th style={{ ...th, textAlign: 'right' }}>Acquiring</th>
                <th style={{ ...th, textAlign: 'right' }}>Reklam</th>
                <th style={{ ...th, textAlign: 'right' }}>İade</th>
                <th style={{ ...th, textAlign: 'right' }}>Diğer</th>
                <th style={{ ...th, textAlign: 'right' }}>Net</th>
                <th style={{ ...th, textAlign: 'right' }}>Marj</th>
                <th style={{ ...th, textAlign: 'right' }}>Kur</th>
                <th style={th}>Dönem</th>
                <th style={th}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const isLoss = parseFloat(o.net_rub || 0) < 0;
                const rowBg = isLoss ? '#fff1f1' : (expanded === o.posting_number ? '#eff6ff' : 'white');
                const num = v => parseFloat(v || 0);
                const fx  = o.fx_rate ? parseFloat(o.fx_rate) : null;

                // "Diğer" = ozon_orders_finance'te AYRI sütunu olmayan GERÇEK kalemler.
                // Artık/denkleştirme DEĞİL: subsidy + logistics + penalty + other (CLAUDE.md).
                // Özdeşlik T16 ile denetleniyor: Gelir + tüm kesintiler = Net (±0,01).
                const digerRub = o.net_rub == null ? null
                  : num(o.subsidy_rub) + num(o.logistics_rub) + num(o.penalty_rub) + num(o.other_rub);

                // ₽/$ toggle TÜM sütunlarda: ayrı _usd kolonu olmayanlar satırın kendi kuruyla çevrilir
                const toUsd = rub => (fx ? rub / fx : null);
                // kapsam=tumu'da tahakkuku olmayan sipariş gelir: finans sütunları BOŞ kalır.
                // Sıfır basmak yanlış olur — kalem yok demek, tutar sıfır demek değil.
                const val = (rubVal, usdCol) => {
                  if (rubVal == null) return '—';
                  const rub = num(rubVal);
                  if (!useUsd) return fmtRub(rub);
                  const usd = usdCol != null ? num(usdCol) : toUsd(rub);
                  return usd == null ? '—' : fmtUsd(usd);
                };
                const dash = (rubVal, usdCol) => (rubVal == null || num(rubVal) === 0 ? '—' : val(rubVal, usdCol));
                const finansVar = o.net_rub != null;

                const urunAdi = (o.products?.[0]?.name) || (o.raw_products?.[0]?.name) || null;
                const urunSayi = (o.products?.length || o.raw_products?.length || 0);
                const adet = o.product_count != null ? o.product_count : null;

                return (
                  <React.Fragment key={o.posting_number}>
                    <tr onClick={() => toggleExpand(o.posting_number)}
                      style={{ cursor: 'pointer', background: rowBg, borderBottom: '1px solid #f0f0f0' }}>
                      <td style={td}>
                        <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{o.posting_number}</div>
                        {o.multi_payout && <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', padding: '1px 5px', borderRadius: 3 }}>+1</span>}
                      </td>
                      <td style={{ ...td, color: '#888', whiteSpace: 'nowrap' }}>{formatDate(o.order_date || o.siparis_tarihi_ham)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDate(o.delivered_at)}</td>
                      {tarihler && EK_TARIHLER.map(c => (
                        <td key={c.key} style={{ ...td, whiteSpace: 'nowrap', color: o[c.key] ? '#111' : '#d1d5db' }}>
                          {formatDate(o[c.key])}
                        </td>
                      ))}
                      <td style={{ ...td, maxWidth: 190 }} title={urunAdi || ''}>
                        <div style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>
                          {urunAdi || '—'}
                        </div>
                        {urunSayi > 1 && <span style={{ fontSize: 10, color: '#888' }}>+{urunSayi - 1} ürün</span>}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: '#555' }}>{adet != null ? adet : '—'}</td>
                      {/* Dolar gelirinde kaynak: Ozon'un aylık satış raporu (belge) varsa o,
                          yoksa CBR çevrimi. Ozon kendi kuruyla çevirdiği için ikisi ~%0,6 ayrışır. */}
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#16a34a' }}
                          title={useUsd
                            ? (o.revenue_usd_kaynak === 'belge'
                                ? 'Ozon satış raporundaki tutar (panelle aynı)'
                                : o.revenue_usd_kaynak === 'api'
                                ? 'Siparişin dolar fiyatı — Ozon panelindeki tutar (satış raporu henüz yayımlanmadı)'
                                : 'Dolar fiyatı bulunamadı — ruble tutarın CBR ile çevrimi (yaklaşık)')
                            : undefined}>
                        {val(o.revenue_rub, o.revenue_usd)}
                        {useUsd && o.revenue_usd_kaynak === 'cbr' && (
                          <span style={{ color: '#9ca3af', fontSize: 10 }}> ≈</span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#dc2626' }}>
                        {dash(o.commission_rub, o.commission_usd)}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#dc2626' }}>
                        {dash(o.shipping_rub, o.shipping_usd)}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#dc2626', fontSize: 11 }}>
                        {dash(o.agency_rub)}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#dc2626', fontSize: 11 }}>
                        {dash(o.acquiring_rub)}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#dc2626', fontSize: 11 }}>
                        {dash(o.marketing_rub)}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#dc2626', fontSize: 11 }}>
                        {dash(o.returns_rub)}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#dc2626', fontSize: 11 }}
                          title={`sübvansiyon ${fmtRub(num(o.subsidy_rub))} · lojistik ${fmtRub(num(o.logistics_rub))} · ceza ${fmtRub(num(o.penalty_rub))} · diğer ${fmtRub(num(o.other_rub))}`}>
                        {!digerRub ? '—' : (useUsd ? (fx ? fmtUsd(digerRub / fx) : '—') : fmtRub(digerRub))}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: isLoss ? '#dc2626' : '#111' }}>
                        {!finansVar ? '—' : (useUsd ? fmtUsd(o.net_usd) : fmtRub(o.net_rub))}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: parseFloat(o.margin_pct||0) < 0 ? '#dc2626' : '#16a34a' }}>
                        {o.margin_pct != null ? fmtPct(o.margin_pct) : '—'}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontSize: 11, color: '#888', cursor: fx ? 'help' : 'default' }}
                          title={fx ? `sipariş tarihi ${formatDate(o.order_date)} · ${o.fx_source === 'cbr' ? 'CBR' : (o.fx_source || 'kur')} ${fmtNum(fx, 4)}` : 'kur atanmadı'}>
                        {fx ? fmtNum(fx, 4) : '—'}
                      </td>
                      <td style={{ ...td, fontSize: 11, whiteSpace: 'nowrap' }}>
                        {o.period_key ? (
                          <button
                            onClick={e => { e.stopPropagation(); if (o.payout_id && onGoToPayout) onGoToPayout(o.payout_id); }}
                            disabled={!o.payout_id}
                            title={o.payout_id ? 'Ödemeler sekmesinde bu dönemi aç' : 'Bu dönemin ödeme kaydı yok'}
                            style={{
                              background: 'none', border: 'none', padding: 0, fontSize: 11, fontFamily: 'inherit',
                              color: o.payout_id ? '#2563eb' : '#555',
                              textDecoration: o.payout_id ? 'underline' : 'none',
                              cursor: o.payout_id ? 'pointer' : 'default'
                            }}>
                            {formatPeriodKey(o.period_key)}
                          </button>
                        ) : '—'}
                      </td>
                      <td style={td}>
                        <Badge status={o.payment_status || o.siparis_statu} />
                        {!o.payment_status && <div style={{ fontSize: 10, color: '#b45309' }}>tahakkuk yok</div>}
                      </td>
                    </tr>
                    {expanded === o.posting_number && (
                      <tr style={{ background: '#f8faff' }}>
                        <td colSpan={18 + (tarihler ? EK_TARIHLER.length : 0)} style={{ padding: '8px 16px 12px 32px' }}>
                          <AccrualDetail accruals={accruals[o.posting_number]} order={o} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: 16, alignItems: 'center' }}>
          <button onClick={() => setPage(1)} disabled={page === 1} style={styles.btnSec}>«</button>
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} style={styles.btnSec}>←</button>
          <span style={{ fontSize: 13, color: '#555' }}>{page} / {totalPages} ({total.toLocaleString('tr-TR')} kayıt)</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} style={styles.btnSec}>→</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={styles.btnSec}>»</button>
        </div>
      )}
    </div>
  );
}

function AccrualDetail({ accruals, order }) {
  if (!accruals) return <div style={{ color: '#888', fontSize: 12 }}>Yükleniyor…</div>;
  if (!accruals.length) return <div style={{ color: '#888', fontSize: 12 }}>Tahakkuk kaydı yok</div>;

  const net = accruals.reduce((s, a) => s + parseFloat(a.line_total_rub || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 6, fontSize: 11, color: '#555' }}>
        <strong>{order.posting_number}</strong> · Sipariş: {formatDate(order.order_date)} · Teslim: {formatDate(order.delivered_at)}
        {order.fx_rate && <span style={{ marginLeft: 10, color: '#888' }}>Kur: {fmtNum(order.fx_rate, 4)} ₽/$</span>}
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%', maxWidth: 680 }}>
        <thead>
          <tr style={{ background: '#e8edf2', borderBottom: '1px solid #cbd5e1' }}>
            <th style={th}>Tip</th>
            <th style={th}>Kategori</th>
            <th style={{ ...th, textAlign: 'right' }}>Birim Fiyat</th>
            <th style={{ ...th, textAlign: 'right' }}>Adet</th>
            <th style={{ ...th, textAlign: 'right' }}>Tahakkuk ₽</th>
            <th style={{ ...th, textAlign: 'right' }}>Toplam ₽</th>
            <th style={{ ...th, textAlign: 'right' }}>Kur</th>
            <th style={{ ...th, textAlign: 'right' }}>$</th>
            <th style={th}>Tarih</th>
          </tr>
        </thead>
        <tbody>
          {accruals.map((a, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={td} title={a.name_en || ''}>{typeLabel(a.type_id, a.name_en)}</td>
              <td style={{ ...td, color: '#888' }}>{a.category || '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{a.seller_price_rub ? fmtRub(a.seller_price_rub) : '—'}</td>
              <td style={{ ...td, textAlign: 'right' }}>{a.quantity || '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: parseFloat(a.accrued_rub||0) < 0 ? '#dc2626' : '#16a34a' }}>{fmtRub(a.accrued_rub)}</td>
              <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtRub(a.line_total_rub)}</td>
              <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#888', fontSize: 10 }}
                  title={a.fx_source === 'cbr' ? 'CBR' : (a.fx_source || '')}>
                {a.fx_rate ? fmtNum(a.fx_rate, 4) : '—'}
              </td>
              <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>{a.line_total_usd ? fmtUsd(a.line_total_usd) : '—'}</td>
              <td style={{ ...td, color: '#888', whiteSpace: 'nowrap' }}>{formatDate(a.accrual_date)}</td>
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700, background: '#f0f4ff' }}>
            <td colSpan={5} style={{ ...td, textAlign: 'right' }}>NET</td>
            <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtRub(net)}</td>
            <td style={td}></td>
            <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>{order.net_usd ? fmtUsd(order.net_usd) : '—'}</td>
            <td style={td}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Sync Butonu ───────────────────────────────────────────────────────────────
function SyncButton() {
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  const sync = async () => {
    setState('loading'); setMsg('');
    try {
      const today = new Date().toISOString().slice(0, 10);
      await apiFetch(`${API}/sync/cashflow`, { method: 'POST', body: JSON.stringify({ from: '2026-05-01', to: today }) });
      setMsg('✓ Sync tamam');
    } catch (e) { setMsg('Hata: ' + e.message); }
    setState('idle');
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {msg && <span style={{ fontSize: 12, color: '#666' }}>{msg}</span>}
      <button onClick={sync} disabled={state === 'loading'} style={styles.btnSec}>
        {state === 'loading' ? 'Sync…' : '↻ Sync'}
      </button>
    </div>
  );
}

// ── Ana Bileşen ───────────────────────────────────────────────────────────────

// ⚠️ "Sipariş Tarihleri" sekmesi KALDIRILDI (23.09.2026) — "Siparişler" ile birleşti.
// Tarih sütunları orada "Tarihler" düğmesiyle açılıyor, kapsam anahtarıyla da
// yoldaki/iptal siparişlere erişiliyor. Ayrı sekme iki ekranın aynı gönderinin
// tahakkuk tarihini iki farklı tablodan (ozon_accruals / ozon_accrual_v2) okumasına
// yol açıyordu; tek uç kaldı: /api/ozon-finance/orders.

// ── Dönem Raporu Tab ──────────────────────────────────────────────────────────
// docs/donem-odeme-raporu.md (v10). Mevcut sekmelere DOKUNMAZ — ayrı uçlardan
// (/api/ozon-payments) ve ayrı tablodan beslenir. Eski sekmelerle rakamları
// KASTEN farklı olabilir (eskisi toplam RUB'u tek kura böler; bu satır bazlı CBR
// ── "Dönem Raporu" sekmesi KALDIRILDI (20.09.2026) ──────────────────────────
// Kaynağı ozon_period_transactions'tı; o tabloyu besleyen /v3/finance/transaction/list
// 17.09.2026'da kapandı ve tablo 24.08.2026'da dondu. Sekme 09-H1'de net 0 ₽, boş kalem
// listesi ve 265.821,92 ₽'lik sahte "denklem tutmuyor" gösteriyordu.
// Yerini Mutabakat sekmesi aldı (canlı kaynaklar + kapılar).
// Sekmenin canlı olan iki yan işi cron'a taşındı: aylık satış raporu (runRealizationSync)
// ve mutabakat verisi (runMutabakatSync) — backend/services/syncService.js.

// Özet kutusu — Maliyet sekmesi ve diğer özet şeritleri kullanır.
// ⚠️ 21.09.2026: "Dönem Raporu" bileşeni silinirken bu yardımcı de silinmişti ve
// Maliyet sekmesi beyaz ekrana düşüyordu (ReferenceError: Kutu is not defined).
function Kutu({ baslik, deger, alt, renk }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8,
                  padding: '10px 14px', minWidth: 180 }}>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{baslik}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: renk || '#111',
                    fontVariantNumeric: 'tabular-nums' }}>{deger}</div>
      {alt ? <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{alt}</div> : null}
    </div>
  );
}

// ── Maliyet sekmesi ─────────────────────────────────────────────────────────
// Kural: TL kaynak, USD türetilmiş. Kur PARTİ başına; global kur yok.
// TCMB kuru yalnız öneri ve ±%5 sapma uyarısıdır, hesaba doğrudan girmez.
const MAL_API = '/api/maliyet';

function MaliyetTab() {
  const [ozet, setOzet]     = useState(null);
  const [rows, setRows]     = useState([]);
  const [suzgec, setSuzgec] = useState('');       // '' | 'kur' | 'tarih'
  const [form, setForm]     = useState({ offer_id: '', adet: '', birim_fiyat_try: '',
                                         kur_usdtry: '', alis_tarihi: '', fatura_no: '', tedarikci: '' });
  const [oneri, setOneri]   = useState(null);
  const [uyari, setUyari]   = useState(null);
  const [durum, setDurum]   = useState(null);
  const [secili, setSecili] = useState([]);
  const [topluKur, setTopluKur] = useState('');
  const [log, setLog]       = useState([]);
  const [logAcik, setLogAcik] = useState(false);
  const [gorunum, setGorunum] = useState('urunler');    // 'urunler' | 'partiler'
  const [urunler, setUrunler] = useState([]);
  const [urunYukleniyor, setUrunYukleniyor] = useState(true);
  const [kapsam, setKapsam]   = useState(null);
  const [ara, setAra]         = useState('');
  const [urunDurum, setUrunDurum] = useState('eksik_satan');
  const [dosya, setDosya]   = useState(null);          // seçilen Excel
  const [onizleme, setOnizleme] = useState(null);      // {ozet, satirlar}
  const [yukleniyor, setYukleniyor] = useState(false);

  const parasal = (v, para = '$') => v == null ? '—'
    : `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${para}`;

  const cek = () => {
    apiFetch(`${MAL_API}/ozet`).then(r => r.json()).then(setOzet).catch(() => {});
    apiFetch(`${MAL_API}/partiler${suzgec ? `?eksik=${suzgec}` : ''}`).then(r => r.json())
      .then(d => setRows(d.rows || [])).catch(() => {});
    const p = new URLSearchParams();
    if (urunDurum) p.set('durum', urunDurum);
    if (ara) p.set('q', ara);
    setUrunYukleniyor(true);
    apiFetch(`${MAL_API}/urunler?${p}`).then(r => r.json())
      .then(d => { setUrunler(d.rows || []); setKapsam(d.kapsam || null); })
      .catch(() => {}).finally(() => setUrunYukleniyor(false));
  };
  useEffect(cek, [suzgec, urunDurum]);                         // eslint-disable-line
  useEffect(() => { const z = setTimeout(cek, 350); return () => clearTimeout(z); }, [ara]); // eslint-disable-line

  // Kur alanı boşken tarihin TCMB kurunu öner
  useEffect(() => {
    if (!form.alis_tarihi) { setOneri(null); return; }
    apiFetch(`${MAL_API}/kur-oneri?tarih=${form.alis_tarihi}`).then(r => r.json()).then(setOneri).catch(() => {});
  }, [form.alis_tarihi]);

  // Girilen kur TCMB'den saparsa uyar (engelleme yok)
  useEffect(() => {
    if (!form.alis_tarihi || !form.kur_usdtry) { setUyari(null); return; }
    const z = setTimeout(() => {
      apiFetch(`${MAL_API}/kur-dogrula?tarih=${form.alis_tarihi}&kur=${form.kur_usdtry}`)
        .then(r => r.json()).then(d => setUyari(d.uyari || null)).catch(() => {});
    }, 400);
    return () => clearTimeout(z);
  }, [form.alis_tarihi, form.kur_usdtry]);

  const usdOnizleme = form.birim_fiyat_try && form.kur_usdtry && Number(form.kur_usdtry) > 0
    ? Number(form.birim_fiyat_try) / Number(form.kur_usdtry) : null;

  const kaydet = async () => {
    setDurum(null);
    const govde = { ...form, adet: Number(form.adet), birim_fiyat_try: Number(form.birim_fiyat_try),
      kur_usdtry: form.kur_usdtry ? Number(form.kur_usdtry) : null,
      kur_kaynak: (oneri && form.kur_usdtry && Number(form.kur_usdtry) === oneri.kur) ? 'tcmb_otomatik' : 'manuel' };
    const r = await apiFetch(`${MAL_API}/parti`, { method: 'POST', body: JSON.stringify(govde) });
    const d = await r.json();
    if (!r.ok) return setDurum({ ok: false, mesaj: d.error });
    setDurum({ ok: true, mesaj: `Parti eklendi · birim ${parasal(d.birim_fiyat_usd)}`, detay: d.uyari });
    setForm(f => ({ ...f, offer_id: '', adet: '', birim_fiyat_try: '' }));
    cek();
  };

  const topluUygula = async () => {
    if (!topluKur || !secili.length) return;
    const r = await apiFetch(`${MAL_API}/toplu-kur`, { method: 'POST',
      body: JSON.stringify({ ids: secili, kur_usdtry: Number(topluKur), kur_kaynak: 'manuel' }) });
    const d = await r.json();
    setDurum(r.ok
      ? { ok: true, mesaj: `${d.degisen} partinin kuru güncellendi`, detay: (d.uyarilar || []).join('\n') }
      : { ok: false, mesaj: d.error });
    setSecili([]); setTopluKur(''); cek();
  };

  const sablonIndir = async () => {
    const r = await apiFetch(`${MAL_API}/sablon`);
    if (!r.ok) return setDurum({ ok: false, mesaj: 'Şablon indirilemedi' });
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a');
    a.href = url; a.download = 'maliyet-sablon.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  // Önce ÖNİZLEME: hiçbir şey yazılmaz. Kullanıcı onaylarsa aynı dosya yazılır.
  const excelGonder = async (f, yaz) => {
    setYukleniyor(true); setDurum(null);
    const fd = new FormData();
    fd.append('dosya', f);
    const token = localStorage.getItem('ozon_token');
    const r = await fetch(`${MAL_API}/toplu-yukle?onizleme=${yaz ? 0 : 1}`, {
      method: 'POST', body: fd, headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const d = await r.json();
    setYukleniyor(false);
    if (!r.ok) {
      setOnizleme(d.ozet ? { ozet: d.ozet, satirlar: d.satirlar || [] } : null);
      return setDurum({ ok: false, mesaj: d.error });
    }
    if (yaz) {
      setDurum({ ok: true, mesaj: `${d.yazilan} parti kaydedildi`, detay: (d.uyarilar || []).join('\n') });
      setOnizleme(null); setDosya(null); cek();
    } else {
      setOnizleme(d);
      setDurum(null);
    }
  };

  const logCek = () => apiFetch(`${MAL_API}/kur-log?limit=50`).then(r => r.json())
    .then(d => setLog(d.rows || [])).catch(() => {});

  return (
    <div style={{ padding: 16 }}>
      {ozet && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <Kutu baslik="Parti" deger={ozet.parti} alt={`${ozet.urun} ürün`} />
          <Kutu baslik="Toplam maliyet" deger={parasal(ozet.toplam_usd)} alt="USD (türetilmiş)" />
          <Kutu baslik="Elle girilen kur" deger={ozet.manuel} alt="parti" />
          <Kutu baslik="TCMB otomatik" deger={ozet.tcmb_otomatik} alt="parti" />
          <Kutu baslik="Kuru eksik" deger={ozet.kursuz} alt="parti" renk={ozet.kursuz ? '#dc2626' : undefined} />
          <Kutu baslik="Alış tarihi yok" deger={ozet.tarihsiz} alt="dönüştürülemez" renk={ozet.tarihsiz ? '#dc2626' : undefined} />
        </div>
      )}

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <b style={{ fontSize: 13 }}>Excel ile toplu yükleme</b>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Her satır bir alış (parti). Adet boş bırakılabilir (1 sayılır, stok düşmez).
            Kur boş bırakılırsa alış tarihinin TCMB kuru kullanılır.
          </span>
          <button onClick={sablonIndir} style={{ ...styles.btnSec, marginLeft: 'auto' }}>Şablon indir</button>
          <label style={{ cursor: yukleniyor ? 'wait' : 'pointer', background: yukleniyor ? '#9ca3af' : '#111827',
                          color: '#fff', padding: '6px 14px', borderRadius: 6, fontSize: 13 }}>
            {yukleniyor ? 'Okunuyor…' : 'Dosya seç'}
            <input type="file" accept=".xlsx" disabled={yukleniyor} style={{ display: 'none' }}
                   onChange={e => { const f = e.target.files[0]; e.target.value = '';
                                    if (f) { setDosya(f); excelGonder(f, false); } }} />
          </label>
        </div>

        {onizleme && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 6, background: '#f9fafb', fontSize: 13 }}>
            <b>Önizleme</b> — {dosya?.name}. Henüz hiçbir şey kaydedilmedi.
            <div style={{ marginTop: 4 }}>
              {onizleme.ozet.gecerli} geçerli · {onizleme.ozet.hatali} hatalı ·
              {' '}{onizleme.ozet.atlanan_bos} boş satır atlandı ·
              toplam {fmtNum(onizleme.ozet.toplam_try)} ₺ = {fmtNum(onizleme.ozet.toplam_usd)} $
              {onizleme.ozet.uyari_sayisi > 0 && <span style={{ color: '#92400e' }}>
                {' '}· {onizleme.ozet.uyari_sayisi} satırda kur sapması</span>}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 8 }}>
              <thead><tr style={{ color: '#6b7280', textAlign: 'left' }}>
                <th style={th}>Satır</th><th style={th}>Ürün</th>
                <th style={{ ...th, textAlign: 'right' }}>Adet</th>
                <th style={{ ...th, textAlign: 'right' }}>Birim TL</th>
                <th style={th}>Alış</th><th style={{ ...th, textAlign: 'right' }}>Kur</th>
                <th style={{ ...th, textAlign: 'right' }}>Birim $</th><th style={th}>Durum</th>
              </tr></thead>
              <tbody>
                {onizleme.satirlar.slice(0, 40).map(x => (
                  <tr key={x.satir} style={{ borderTop: '1px solid #f3f4f6',
                                             color: x.hatalar.length ? '#991b1b' : '#374151' }}>
                    <td style={td}>{x.satir}</td>
                    <td style={td}>{x.offer_id}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{x.adet ?? '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{x.birim_try == null ? '—' : fmtNum(x.birim_try)}</td>
                    <td style={td}>{x.alis_tarihi ? formatDate(x.alis_tarihi) : '—'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{x.kur == null ? '—' : Number(x.kur).toFixed(4)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                      {x.birim_usd == null ? '—' : fmtNum(x.birim_usd)}</td>
                    <td style={td}>
                      {x.hatalar.length ? x.hatalar.join(', ')
                        : <>{x.kur_kaynak === 'tcmb_otomatik' ? (x.kur_not || 'TCMB') : 'elle girilen kur'}
                           {x.uyari ? ' ⚠ sapma' : ''}</>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {onizleme.satirlar.length > 40 && (
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                …ilk 40 satır gösteriliyor ({onizleme.satirlar.length} satır okundu)</div>
            )}
            <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => excelGonder(dosya, true)}
                      disabled={yukleniyor || onizleme.ozet.hatali > 0}
                      title={onizleme.ozet.hatali > 0 ? 'Hatalı satır varken yükleme yapılmaz — düzeltip tekrar seçin' : ''}
                      style={{ ...styles.btnPri, opacity: onizleme.ozet.hatali > 0 ? 0.5 : 1 }}>
                Onayla ve {onizleme.ozet.gecerli} partiyi kaydet
              </button>
              <button onClick={() => { setOnizleme(null); setDosya(null); }} style={styles.btnSec}>Vazgeç</button>
              {onizleme.ozet.hatali > 0 && <span style={{ fontSize: 12, color: '#991b1b' }}>
                Hatalı satırlar düzeltilmeden kayıt yapılmaz (yarım maliyet, yanlış kâr demektir).</span>}
            </div>
          </div>
        )}
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 14 }}>
        <b style={{ fontSize: 13 }}>Parti ekle</b>
        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>
          TL fiyat ve kur siz girersiniz; dolar karşılığı sistemde hesaplanır. Kur her partinin kendi alış kurudur.
          Adet boş bırakılabilir — stok düşmez, yalnız birden çok parti varsa ortalamanın ağırlığıdır.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="offer_id" value={form.offer_id}
                 onChange={e => setForm(f => ({ ...f, offer_id: e.target.value }))}
                 style={{ ...styles.input, width: 260 }} />
          <input placeholder="adet (boş=1)" type="number" value={form.adet}
                 onChange={e => setForm(f => ({ ...f, adet: e.target.value }))} style={{ ...styles.input, width: 80 }} />
          <input placeholder="birim TL" type="number" value={form.birim_fiyat_try}
                 onChange={e => setForm(f => ({ ...f, birim_fiyat_try: e.target.value }))} style={{ ...styles.input, width: 110 }} />
          <input type="date" value={form.alis_tarihi}
                 onChange={e => setForm(f => ({ ...f, alis_tarihi: e.target.value }))} style={{ ...styles.input, width: 150 }} />
          <input placeholder="kur USD/TRY" type="number" step="0.0001" value={form.kur_usdtry}
                 onChange={e => setForm(f => ({ ...f, kur_usdtry: e.target.value }))} style={{ ...styles.input, width: 130 }} />
          {oneri && oneri.kur != null && (
            <button onClick={() => setForm(f => ({ ...f, kur_usdtry: String(oneri.kur) }))}
                    style={{ ...styles.btnSec }} title={oneri.not || `${oneri.kur_tarihi} TCMB döviz satış`}>
              TCMB {oneri.kur} ↵
            </button>
          )}
          <input placeholder="fatura no" value={form.fatura_no}
                 onChange={e => setForm(f => ({ ...f, fatura_no: e.target.value }))} style={{ ...styles.input, width: 130 }} />
          <input placeholder="tedarikçi" value={form.tedarikci}
                 onChange={e => setForm(f => ({ ...f, tedarikci: e.target.value }))} style={{ ...styles.input, width: 150 }} />
          <span style={{ fontSize: 13, color: '#065f46', fontWeight: 700 }}>
            {usdOnizleme != null ? `= ${parasal(usdOnizleme)} / adet` : ''}</span>
          <button onClick={kaydet} disabled={!form.offer_id || !form.birim_fiyat_try}
                  style={{ ...styles.btnPri, marginLeft: 'auto' }}>Kaydet</button>
        </div>
        {oneri && oneri.not && <div style={{ fontSize: 11, color: '#92400e', marginTop: 6 }}>{oneri.not}</div>}
        {uyari && <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>⚠ {uyari}</div>}
        {durum && (
          <div style={{ marginTop: 8, padding: 8, borderRadius: 6, fontSize: 13, whiteSpace: 'pre-line',
                        background: durum.ok ? '#f0fdf4' : '#fef2f2', color: durum.ok ? '#065f46' : '#991b1b' }}>
            {durum.ok ? '✓ ' : '✗ '}{durum.mesaj}{durum.detay ? `\n${durum.detay}` : ''}
          </div>
        )}
      </div>

      {/* Görünüm: ÜRÜN bazlı (neyin maliyeti eksik) · PARTİ bazlı (tek tek alışlar) */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        {[['urunler', 'Ürünler'], ['partiler', 'Partiler']].map(([k, l]) => (
          <button key={k} onClick={() => setGorunum(k)} style={{ ...styles.btnSec,
            fontWeight: gorunum === k ? 700 : 400,
            background: gorunum === k ? '#111827' : 'white', color: gorunum === k ? 'white' : '#374151' }}>{l}</button>
        ))}
        {kapsam && gorunum === 'urunler' && (
          <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>
            <Ipucu baslik="Kapsam" metin={ACIKLAMA.m_kapsam} />
            {' '}Son 90 günde satan <b>{kapsam.satan}</b> üründen <b>{kapsam.satan_maliyetli}</b> tanesinin maliyeti var ·
            {' '}cironun <b>%{fmtNum(kapsam.ciro_kapsanan_oran || 0, 1)}</b>'i kâr hesabına giriyor
          </span>
        )}
      </div>

      {gorunum === 'urunler' && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <input placeholder="ürün adı, kod veya SKU…" value={ara} onChange={e => setAra(e.target.value)}
                   style={{ ...styles.input, width: 240 }} />
            {[['eksik_satan', 'satan + maliyeti eksik'], ['eksik', 'maliyeti eksik'],
              ['var', 'maliyeti var'], ['satan', 'satanlar'], ['skusuz', 'SKU yok'], ['', 'tümü']].map(([k, l]) => (
              <button key={k} onClick={() => setUrunDurum(k)} style={{ ...styles.btnSec,
                background: urunDurum === k ? '#2563eb' : 'white', color: urunDurum === k ? 'white' : '#374151' }}>{l}</button>
            ))}
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              {urunYukleniyor ? 'yükleniyor…' : `${urunler.length} ürün`} · satıra tıklayınca aşağıdaki forma taşınır</span>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 18 }}>
            <thead><tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ ...th, textAlign: 'left' }}>SKU</th>
              <th style={{ ...th, textAlign: 'left' }}>Ürün</th>
              <th style={{ ...th, textAlign: 'right' }}>90 gün satış</th>
              <th style={{ ...th, textAlign: 'right' }}>Stok</th>
              <th style={{ ...th, textAlign: 'right' }}>Parti</th>
              <th style={{ ...th, textAlign: 'right' }}>
                Birim maliyet $<Ipucu baslik="Birim maliyet $" metin={ACIKLAMA.m_birim_usd} /></th>
              <th style={th}>Son alış</th>
              <th style={th}>Kur kaynağı</th>
              <th style={th}>Durum</th>
            </tr></thead>
            <tbody>
              {urunler.map(u => (
                <tr key={(u.sku || '') + u.kodlar}
                    onClick={() => setForm(f => ({ ...f, offer_id: String(u.kodlar).split(' · ')[0] }))}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                             background: String(u.kodlar).split(' · ')[0] === form.offer_id ? '#eff6ff' : undefined }}>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>
                    {u.sku || <span style={{ color: '#dc2626' }}>yok</span>}</td>
                  <td style={td}>{u.urun}
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>
                      {u.kodlar}{u.kod_sayisi > 1 ? ` (${u.kod_sayisi} kod, tek ürün)` : ''}</div></td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {u.satis_90 ? u.satis_90.toLocaleString('tr-TR') : '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{u.stok ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{u.parti || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {u.birim_usd == null ? '—' : parasal(u.birim_usd)}</td>
                  <td style={td}>{u.son_alis ? formatDate(u.son_alis) : '—'}</td>
                  <td style={{ ...td, color: '#6b7280' }}>
                    {u.parti ? `${u.manuel ? u.manuel + ' elle' : ''}${u.manuel && u.tcmb ? ' · ' : ''}${u.tcmb ? u.tcmb + ' TCMB' : ''}${u.kursuz ? ` · ${u.kursuz} kursuz` : ''}` : '—'}</td>
                  <td style={td}>
                    {!u.sku
                      ? <span style={{ color: '#92400e' }}>SKU yok — kâr hesabına giremez</span>
                      : u.parti === 0
                      ? <span style={{ color: u.satis_90 > 0 ? '#dc2626' : '#9ca3af' }}>
                          maliyet yok{u.satis_90 > 0 ? ' — kâr hesaplanamıyor' : ''}</span>
                      : u.kursuz > 0 ? <span style={{ color: '#92400e' }}>kuru eksik parti var</span>
                      : <span style={{ color: '#065f46' }}>tamam</span>}
                  </td>
                </tr>
              ))}
              {!urunler.length && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                {urunYukleniyor ? 'yükleniyor…' : 'Ürün yok'}</td></tr>}
            </tbody>
          </table>
        </>
      )}

      {gorunum === 'partiler' && (<>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        {[['', 'tümü'], ['kur', 'kuru eksik'], ['tarih', 'alış tarihi yok']].map(([k, l]) => (
          <button key={k} onClick={() => setSuzgec(k)} style={{ ...styles.btnSec,
            background: suzgec === k ? '#2563eb' : 'white', color: suzgec === k ? 'white' : '#374151' }}>{l}</button>
        ))}
        {secili.length > 0 && (
          <>
            <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 12 }}>{secili.length} parti seçili — toplu kur:</span>
            <input placeholder="kur" type="number" step="0.0001" value={topluKur}
                   onChange={e => setTopluKur(e.target.value)} style={{ ...styles.input, width: 120 }} />
            <button onClick={topluUygula} disabled={!topluKur} style={styles.btnPri}>Uygula</button>
          </>
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
          <th style={th}></th>
          <th style={{ ...th, textAlign: 'left' }}>Ürün</th>
          <th style={th}>Alış</th><th style={th}>Fatura</th>
          <th style={{ ...th, textAlign: 'right' }}>Adet</th>
          <th style={{ ...th, textAlign: 'right' }}>
            Birim TL<Ipucu baslik="Birim TL" metin={ACIKLAMA.m_birim_try} /></th>
          <th style={{ ...th, textAlign: 'right' }}>
            Kur<Ipucu baslik="Kur" metin={ACIKLAMA.m_kur} /></th>
          <th style={th}>Kur kaynağı<Ipucu baslik="Kur kaynağı" metin={ACIKLAMA.m_kur_kaynak} /></th>
          <th style={{ ...th, textAlign: 'right' }}>
            Birim $<Ipucu baslik="Birim $" metin={ACIKLAMA.m_birim_usd} /></th>
          <th style={{ ...th, textAlign: 'right' }}>Toplam $</th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={td}>
                <input type="checkbox" checked={secili.includes(r.id)}
                       onChange={e => setSecili(x => e.target.checked ? [...x, r.id] : x.filter(i => i !== r.id))} />
              </td>
              <td style={td}>{r.urun || r.offer_id}
                {r.kur_degisikligi > 0 && <span style={{ fontSize: 10, color: '#92400e' }}> · kur {r.kur_degisikligi}× değişti</span>}
              </td>
              <td style={td}>{r.alis_tarihi ? formatDate(r.alis_tarihi) : <span style={{ color: '#dc2626' }}>yok</span>}</td>
              <td style={td}>{r.fatura_no || '—'}</td>
              <td style={{ ...td, textAlign: 'right' }}>{r.adet}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{parasal(r.birim_fiyat_try, '₺')}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {r.kur_usdtry == null ? <span style={{ color: '#dc2626' }}>eksik</span> : Number(r.kur_usdtry).toFixed(4)}</td>
              <td style={{ ...td, color: r.kur_kaynak === 'manuel' ? '#374151' : '#6b7280' }}>
                {r.kur_kaynak === 'manuel' ? 'elle' : r.kur_kaynak === 'tcmb_otomatik' ? 'TCMB' : '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {parasal(r.birim_fiyat_usd)}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{parasal(r.toplam_usd)}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 24 }}>
            Parti yok</td></tr>}
        </tbody>
      </table>
      </>)}

      <div style={{ marginTop: 12, fontSize: 12 }}>
        <button onClick={() => { setLogAcik(a => !a); if (!logAcik) logCek(); }}
                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: 12 }}>
          {logAcik ? '▾' : '▸'} kur değişiklik günlüğü
        </button>
        {logAcik && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 8 }}>
            <thead><tr style={{ color: '#6b7280', textAlign: 'left' }}>
              <th style={th}>Zaman</th><th style={th}>Ürün</th><th style={th}>Kur</th>
              <th style={th}>Birim $</th><th style={th}>Kullanıcı</th><th style={th}>Not</th>
            </tr></thead>
            <tbody>
              {log.map(l => (
                <tr key={l.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={td}>{l.zaman}</td>
                  <td style={td}>{l.offer_id}</td>
                  <td style={td}>{l.eski_kur == null ? '—' : Number(l.eski_kur).toFixed(4)} → {Number(l.yeni_kur).toFixed(4)}
                    <span style={{ color: '#9ca3af' }}> ({l.eski_kaynak || '—'} → {l.yeni_kaynak})</span></td>
                  <td style={td}>{parasal(l.eski_usd)} → {parasal(l.yeni_usd)}</td>
                  <td style={td}>{l.kullanici}</td>
                  <td style={td}>{l.aciklama || '—'}</td>
                </tr>
              ))}
              {!log.length && <tr><td style={{ ...td, color: '#9ca3af' }} colSpan={6}>kayıt yok</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// docs/ozon-mutabakat-modeli-v2.md §17. YENİ ekran; diğer sekmelere DOKUNMAZ.
// İki alt sekme: Belgeler (kova + sağlama katmanları) · Siparişler (gönderi bazlı).
const MUT_API = '/api/ozon-mutabakat';

// Kova kodları → Türkçe karşılık + hangi belgeye karşılık geldiği (§6)
// ── Açıklama baloncuğu ──────────────────────────────────────────────────────
// Bu ekranlara ilk kez bakan biri her kalemin ne olduğunu ve nasıl hesaplandığını
// buradan okuyabilmeli. Üzerine gelince kısa özet (title), tıklayınca tam metin.
function Ipucu({ metin, baslik }) {
  const [acik, setAcik] = useState(false);
  if (!metin) return null;
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: 4 }}>
      <button onClick={e => { e.stopPropagation(); setAcik(a => !a); }}
              title={metin}
              style={{ width: 15, height: 15, lineHeight: '13px', borderRadius: '50%',
                       border: '1px solid #cbd5e1', background: acik ? '#2563eb' : 'white',
                       color: acik ? 'white' : '#64748b', fontSize: 10, fontWeight: 700,
                       cursor: 'pointer', padding: 0, verticalAlign: 'middle' }}>i</button>
      {acik && (
        <>
          <span onClick={e => { e.stopPropagation(); setAcik(false); }}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <span onClick={e => e.stopPropagation()}
                style={{ position: 'absolute', zIndex: 41, top: 20, left: -8, width: 340,
                         background: 'white', border: '1px solid #e5e7eb', borderRadius: 8,
                         boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: '10px 12px',
                         fontSize: 12, lineHeight: 1.5, color: '#374151', whiteSpace: 'pre-line',
                         fontWeight: 400, textAlign: 'left' }}>
            {baslik && <div style={{ fontWeight: 700, marginBottom: 4, color: '#111' }}>{baslik}</div>}
            {metin}
          </span>
        </>
      )}
    </span>
  );
}

// Ekrandaki her kalemin açıklaması. Sözlük tek yerde — aynı metin birden çok
// ekranda kullanılıyor (kova tablosu, sipariş sütunları, dönem kârı).
const ACIKLAMA = {
  // ── Kovalar (kalem grupları)
  REAL:  'Satış hasılatı. Ozon\'un aylık "Satış raporu"ndaki tutar: ürünün satış fiyatı × adet.\nİadeler negatif satır olarak düşülür.',
  AKT:   'Hizmet aktı. Ozon\'un size kestiği hizmet faturası: satış komisyonu, lojistik,\nmarka tanıtımı, hızlı yorum gibi kalemler. Aydan aya iki akt gelir (ayın ortası ve sonu).',
  PEREV: 'Yeniden faturalama. Ozon\'un sizin adınıza ödeyip size yansıttığı masraflar:\nuluslararası kargo ve teslim noktası ücretleri.',
  EKV:   'Tahsilat komisyonu (acquiring). Müşteri kartla ödediğinde alınan çekim ücreti;\nsatış tutarının ortalama %1,5\'i kadar.\nSİPARİŞ BAZLIDIR: panel tahakkuk raporu yüklendiğinde ilgili gönderiye bağlanır ve\no siparişin kârından düşer. Rapor yoksa gönderisiz kalır ve dönem gideri olarak görünür.',
  ZV:    'Ozon Yıldızlar programı. Ürünlerinizin "yıldızlı" listelerde görünmesi için alınan ücret.',
  AGENT: 'Acente raporu. Ozon\'un müşteriye yaptığı kısmi tazminatın size yansıtılması.',
  DONEM: 'Dönem gideri. Hiçbir siparişe dağıtılamayan kalemler: Premium abonelik,\nsipariş başına tanıtım kampanyası (CPO), imha ücreti. Sipariş kârına GİRMEZ.',
  KOMP:  'Tazminat geliri. Ozon kaynaklı hasar veya kayıp için size ödenen bedel.',
  IPTAL: 'İptal maliyeti. Teslim edilmemiş gönderilerin ters lojistik ve iade işleme ücretleri.',
  PUAN:  'İndirim puanları. Ozon\'un müşteriye verdiği puan indirimi; bir kısmı akt borcunuzdan düşülür,\nartanı "prim" olarak hesabınıza geçer.',
  HARIC: 'Mutabakat dışı. Ozon\'un size yaptığı ÖDEMENİN kendisi. Gider değildir,\nbu yüzden kâr/zarar hesabına girmez.',
  'EŞLEŞMEYEN': 'Ozon yeni bir kalem tipi göndermiş ve sistemde karşılığı yok.\nDoğru gruba eklenene kadar mutabakat bloklanır — çünkü tutar sessizce kaybolmamalı.',

  // ── Sipariş sütunları
  hasilat_belge: 'Ozon\'un aylık satış raporundaki dolar tutarı. Çevrim yok, belgede ne yazıyorsa o.\nRapor ay kapandıktan sonra yayımlanır; o zamana kadar boş görünür.',
  net_rub: 'Bu gönderiden elinizde kalan ruble: satış hasılatı eksi Ozon\'un tüm kesintileri.\nÜrün maliyeti BURAYA DAHİL DEĞİL.',
  net_usd: 'Aynı tutarın doları. Çevrim, siparişin kendi kuruyla yapılır:\nOzon satışı dolarla fiyatlayıp rubleye çevirdiği için o kur kullanılır (ruble tutar ÷ dolar fiyat).',
  maliyet_usd: 'Bu gönderideki ürünlerin size maliyeti. SKU başına ağırlıklı ortalama alış maliyeti × satılan adet.\nMaliyet sekmesinden girdiğiniz TL fiyatlar, partinin kendi kuruyla dolara çevrilir.',
  kar_usd: 'Brüt kâr = Ozon sonrası net − ürün maliyeti.\nGönderideki ürünlerden birinin maliyeti girilmemişse BOŞ kalır; yarım maliyetle kâr yanıltır.\nTürkiye tarafı giderler (nakliye, gümrük, genel gider) bu rakamda YOKTUR.',

  puan_usd: 'Bu siparişin ne kadarı Ozon\'un puanıyla ödenmiş.\nOzon müşteriye kendi puanıyla indirim yaptırır; satış fiyatı tahakkuka BRÜT girer,\nama Ozon\'un nakit yazdığı tutar bu kadar düşüktür.\nDönem toplamı doğrudur (gelir de gider de brüt), ama puanı yüksek bir siparişin kârı\niyimser görünür — o puan başka siparişlerin hizmet ücretlerini kapatmakta kullanılır.\nYalnızca aylık satış raporu yayımlanmış dönemlerde dolu gelir.',

  // ── Dönem kârı
  dk_siparis: 'Maliyeti bilinen gönderilerin kârı toplamı: (Ozon sonrası net − ürün maliyeti).\nSadece maliyeti girilmiş gönderiler sayılır; kapsam oranı yanında yazar.',
  dk_donem: 'Hiçbir siparişe bağlanamayan Ozon giderleri:\n• Premium abonelik (aylık sabit ücret)\n• CPO kampanyası (kampanya bazında günlük faturalanır, sipariş bazlı değil)\n• imha, kompanzasyon\n• satışı eşleşmeyen tahsilat komisyonu (siparişi veri aralığımız dışında ya da iptal)\nSipariş satırlarında görünmezler ama parayı siz ödersiniz.',
  dk_maliyetsiz: 'Ürün maliyeti girilmemiş gönderilerin Ozon sonrası neti.\nKâr hesabına girmezler (maliyet bilinmeden kâr yazmak yanıltır), ama dönemin parçasıdırlar.\nMaliyet girdikçe bu satır küçülür ve kâra taşınır.',
  dk_ozon: 'Ozon sonrası kâr = sipariş kârı − dönem giderleri.\nBu, Ozon ile ilişkinizden kalan tutardır.\nTürkiye tarafı (nakliye, gümrük, banka, personel, depo) HÂLÂ DÜŞÜLMEMİŞTİR.',
  dk_kapsam: 'Kaç gönderinin ürün maliyeti girilmiş. Maliyeti olmayan gönderiler kâr hesabına hiç girmez;\noran düşükse kâr rakamı dönemin tamamını temsil etmez.',

  // ── Kapılar
  kapi: 'Sağlama kapısı: sistemin hesabını Ozon\'un resmî belgesiyle karşılaştırır.\n"Geçti" = iki taraf kuruşuna aynı. "Kaldı" = fark var, sebebi bulunmalı.\n"Veri yok" = karşılaştıracak belge henüz elimizde değil, o yüzden hesap yapılmadı.',

  // ── Maliyet ekranı
  m_birim_try: 'Ürünün size TL maliyeti (birim fiyat). Kaynak budur; dolar tutarı bundan türetilir.',
  m_kur: 'Bu partinin alış kuru. Her partinin kendi kuru vardır — genel bir kur kullanılmaz.\nBoş bırakılırsa alış tarihindeki TCMB döviz satış kuru yazılır.',
  m_kur_kaynak: '"elle" = kuru siz yazdınız · "TCMB" = alış tarihinin TCMB kuru otomatik kullanıldı.\nKuru sonradan düzeltirseniz dolar maliyeti yeniden hesaplanır ve değişiklik günlüğe yazılır.',
  m_birim_usd: 'Dolar maliyet = TL fiyat ÷ kur. Veritabanı hesaplar, elle yazılamaz;\nbu yüzden TL ile dolar hiçbir zaman birbirinden kopamaz.',
  m_kapsam: 'Son 90 günde satan ürünlerden kaçının maliyeti girilmiş ve cironun yüzde kaçı kâr hesabına giriyor.\nCiro ağırlıklıdır: en çok satan ürünleri girmek oranı hızla yükseltir.',
};

// Ozon'un Rusça kalem adları → Türkçe. Kova detayında ham Rusça okunmuyordu (23.09.2026).
// Anahtar: rapordaki `tip` alanı; grup adları ayrı sözlükte.
const TIP_TR = {
  'Выручка':                                 'Satış hasılatı',
  'Вознаграждение за продажу':               'Satış komisyonu',
  'Возврат вознаграждения':                  'Komisyon iadesi',
  'Возврат выручки':                         'Hasılat iadesi',
  'Баллы за скидки':                         'İndirim puanları',
  'Программы партнёров':                     'Partner programları',
  'Эквайринг':                               'Tahsilat komisyonu (kart)',
  'Услуги международной доставки':           'Uluslararası kargo',
  'Агентское вознаграждение Ozon':           'Uluslararası acentelik',
  'Звёздные товары':                         'Yıldızlı ürünler programı',
  'Доставка до места выдачи':                'Teslim noktasına taşıma',
  'Доставка до места выдачи силами Ozon':    'Teslim noktasına taşıma (Ozon)',
  'Логистика':                               'Lojistik',
  'Обратная логистика':                      'Ters lojistik (iade)',
  'Обработка возвратов, отмен и невыкупов партнёрами': 'İade/iptal işleme',
  'Услуги по заключению договора на организацию международной перевозки': 'Nakliye sözleşmesi hizmeti',
  'Электронная услуга подключения к логистической Платформе Ozon': 'Lojistik platform bağlantı hizmeti',
  'Продвижение бренда':                      'Marka tanıtımı',
  'Продвижение товара':                      'Ürün tanıtımı (kampanya)',
  'Продвижение с оплатой за заказ':          'Sipariş başına tanıtım (CPO)',
  'Оплата за клик':                          'Tıklama başına reklam (CPC)',
  'Ускоренный сбор отзывов':                 'Hızlı yorum toplama',
  'Подписка Premium':                        'Premium abonelik',
  'Подписка Premium Pro':                    'Premium Pro abonelik',
  'Подписка Premium Pro (процент)':          'Premium Pro (satış yüzdesi)',
  'Утилизация товара':                       'Ürün imha',
  'Утилизация':                              'Ürün imha',
  'Обработка товара':                        'Mal kabul işleme',
  'Компенсация':                             'Tazminat',
  'Товарная компенсация':                    'Ürün tazminatı',
  'Брак по вине Ozon на складе':             'Ozon deposunda hasar',
  'Перечисления частичных компенсаций покупателям': 'Müşteriye kısmi tazminat',
  'Инвентаризация взаиморасчетов':           'Ödeme (mutabakat kapanışı)',
};

const GRUP_TR = {
  'Продажи':                     'Satışlar',
  'Возвраты':                    'İadeler',
  'Вознаграждение Ozon':         'Ozon komisyonu',
  'Услуги доставки':             'Teslimat hizmetleri',
  'Услуги партнёров':            'Partner hizmetleri',
  'Продвижение и реклама':       'Tanıtım ve reklam',
  'Компенсации и декомпенсации': 'Tazminatlar',
  'Другие услуги и штрафы':      'Diğer hizmet ve cezalar',
  'Прочие начисления':           'Diğer tahakkuklar',
};

const tipTr  = (t) => TIP_TR[String(t || '').trim()] || null;
const grupTr = (g) => GRUP_TR[String(g || '').trim()] || null;

const KOVA_BILGI = {
  REAL:  ['Satış hasılatı',        'Отчет о реализации · satış geliri + partner programları'],
  PUAN:  ['İndirim puanları',      'Баллы за скидки · aktı kapatan puan, artanı prim olarak ödenir'],
  PEREV: ['Yeniden faturalama',    'Отчет о перевыставлении · uluslararası kargo, teslim noktası'],
  EKV:   ['Tahsilat komisyonu',    'Эквайринг · kart çekim ücreti, sipariş seviyesinde'],
  ZV:    ['Ozon Yıldızlar',        'Акт ЗВ · Звёздные товары programı'],
  AKT:   ['Hizmet aktı',           'Акт выполненных работ · komisyon, lojistik, marka tanıtımı'],
  AGENT: ['Acente raporu',         'Агентский отчет · müşteriye kısmi tazminat aktarımı'],
  KOMP:  ['Tazminat (gelir)',      'Компенсации · Ozon kaynaklı hasar/kayıp karşılığı'],
  DONEM: ['Dönem gideri',          'Gönderiye dağıtılamaz: Premium abonelik, kampanya, hızlı yorum'],
  IPTAL: ['İptal maliyeti',        'Ters lojistik, iade işleme · teslim edilmemiş gönderilere ait'],
  HARIC: ['Mutabakat dışı',        'Инвентаризация · ödemenin KENDİSİ, gider değil — P&L\'e girmez'],
  DIGER: ['Tanımsız',              'Haritada karşılığı var ama sınıflandırılmamış'],
  'EŞLEŞMEYEN': ['Eşleşmeyen',     'Ozon yeni bir tip getirdi — mutabakat bloklanır, haritaya eklenmeli'],
};

const DURUM_ROZET = {
  gecti:    { im: '✓', renk: '#16a34a', ad: 'geçti' },
  kaldi:    { im: '✗', renk: '#dc2626', ad: 'KALDI' },
  gecersiz: { im: '⊘', renk: '#b45309', ad: 'geçersiz' },
  usd_de:   { im: '◐', renk: '#6b7280', ad: "USD'de doğrulanır" },
  veri_yok: { im: '—', renk: '#9ca3af', ad: 'veri yok' },
};

function MutabakatTab() {
  const [donemler, setDonemler] = useState([]);
  const [secili, setSecili]     = useState('');
  const [altTab, setAltTab]     = useState('belgeler');
  const [ozet, setOzet]         = useState(null);
  const [hata, setHata]         = useState('');
  const [loading, setLoading]   = useState(true);
  const [tazele, setTazele]     = useState(0);   // belge yüklenince özeti yeniden çek

  useEffect(() => {
    apiFetch(`${MUT_API}/periods`).then(r => r.json()).then(d => {
      const l = d.periods || [];
      setDonemler(l);
      setSecili(p => p || (l[0] && l[0].donem) || '');
      setLoading(false);
    }).catch(e => { setHata(String(e.message || e)); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!secili) return;
    setHata('');
    apiFetch(`${MUT_API}/${secili}/ozet`).then(async r => {
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      return d;
    }).then(setOzet).catch(e => { setOzet(null); setHata(String(e.message || e)); });
  }, [secili, tazele]);

  if (loading) return <div style={styles.loading}>Yükleniyor…</div>;

  const parasal = (v, para = '₽') => v == null ? '—'
    : `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${para}`;
  const d = donemler.find(x => x.donem === secili);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Dönem:</span>
        <select value={secili} onChange={e => setSecili(e.target.value)} style={{ ...styles.input, minWidth: 260 }}>
          {donemler.map(x => (
            <option key={x.donem} value={x.donem}>
              {x.donem} — {x.panel_raporu_var ? `${x.rapor_satir.toLocaleString('tr-TR')} satır` : 'panel raporu YOK'}
            </option>
          ))}
        </select>

        {[['belgeler', 'Belgeler'], ['siparisler', 'Siparişler']].map(([k, l]) => (
          <button key={k} onClick={() => setAltTab(k)} style={{
            ...styles.btnSec, background: altTab === k ? '#2563eb' : 'white',
            color: altTab === k ? 'white' : '#374151' }}>{l}</button>
        ))}

        {/* §22a: sistem API + belge yüklemesiyle çalışır — eksik belge AÇIKÇA görünür */}
        {d && !d.panel_raporu_var && (
          <span style={{ fontSize: 12, color: '#b45309', fontWeight: 700 }}>
            ⚠ panel tahakkuk raporu yüklenmemiş
          </span>
        )}
      </div>

      {hata && <div style={{ padding: 12, background: '#fef2f2', color: '#991b1b', fontSize: 13 }}>{hata}</div>}

      {altTab === 'belgeler' && ozet && (
        <MutBelgeler ozet={ozet} parasal={parasal} onYuklendi={() => setTazele(x => x + 1)} />
      )}
      {altTab === 'siparisler' && <MutSiparisler donem={secili} parasal={parasal} />}
    </div>
  );
}

// Belge yükleme. Tip ve dönem DOSYADAN çıkar — kullanıcıya sordurmuyoruz,
// çünkü elle seçilen dönem yanlış yere yazma riskidir.
function MutBelgeYukle({ onYuklendi }) {
  const [durum, setDurum]   = useState(null);   // {ok, mesaj, detay}
  const [calisiyor, setCal] = useState(false);
  const [liste, setListe]   = useState([]);
  const [aylar, setAylar]   = useState([]);
  const [acik, setAcik]     = useState(false);

  const listeyiCek = () => apiFetch(`${MUT_API}/belgeler`).then(r => r.json())
    .then(d => { setListe(d.rows || []); setAylar(d.mutabakat_aylari || []); }).catch(() => {});
  useEffect(() => { listeyiCek(); }, []);

  const gonder = async (dosya) => {
    if (!dosya) return;
    setCal(true); setDurum(null);
    try {
      const fd = new FormData();
      fd.append('dosya', dosya);
      // ⚠️ Content-Type ELLE VERİLMEZ — tarayıcı multipart boundary'sini kendi yazar.
      // apiFetch json'a sabitlediği için burada kendi fetch'imizi kullanıyoruz.
      const token = localStorage.getItem('ozon_token');
      const r = await fetch(`${MUT_API}/belge`, {
        method: 'POST', body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const d = await r.json();
      if (!r.ok) {
        const fark = d.fark ? [
          ...d.fark.yalniz_panel.map(x => `yalnız dosyada: ${x}`),
          ...d.fark.yalniz_ledger.map(x => `yalnız kayıtta: ${x}`)].join('\n') : null;
        setDurum({ ok: false, mesaj: d.error || `HTTP ${r.status}`, detay: fark || d.detay });
      }
      else {
        setDurum({ ok: true, mesaj: `${d.tip} yüklendi · dönem ${d.donem || d.donem_metni} · ${d.satir} satır`
          + (d.toplam_usd != null ? ` · ${fmtNum(d.toplam_usd)} $` : '')
          + (d.toplam_rub != null ? ` · ${fmtNum(d.toplam_rub)} ₽` : '')
          + (d.kapanis_rub != null ? ` · kapanış ${fmtNum(d.kapanis_rub)} ₽` : ''),
          detay: d.not || (d.bilinmeyen && d.bilinmeyen.length ? `tanınmayan satır: ${d.bilinmeyen.join('; ')}` : null) });
        listeyiCek(); if (onYuklendi) onYuklendi();
      }
    } catch (e) { setDurum({ ok: false, mesaj: String(e.message || e) }); }
    setCal(false);
  };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 13 }}>Belge yükle</b>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          Döviz ödeme raporu · Akt/UTD (PDF) · Tahakkuk · Mutabakat (tek ay) · İndirim puanları (XLSX) — tip ve dönem dosyadan okunur
        </span>
        <label style={{ marginLeft: 'auto', cursor: calisiyor ? 'wait' : 'pointer',
                        background: calisiyor ? '#9ca3af' : '#111827', color: '#fff',
                        padding: '6px 14px', borderRadius: 6, fontSize: 13 }}>
          {calisiyor ? 'Yükleniyor…' : 'Dosya seç'}
          <input type="file" accept=".pdf,.xlsx" disabled={calisiyor}
                 style={{ display: 'none' }}
                 onChange={e => { gonder(e.target.files[0]); e.target.value = ''; }} />
        </label>
      </div>

      {durum && (
        <div style={{ marginTop: 10, padding: 9, borderRadius: 6, fontSize: 13,
                      background: durum.ok ? '#f0fdf4' : '#fef2f2',
                      color: durum.ok ? '#065f46' : '#991b1b' }}>
          {durum.ok ? '✓ ' : '✗ '}{durum.mesaj}
          {durum.detay && <div style={{ marginTop: 4, fontSize: 12, opacity: .85, whiteSpace: 'pre-line' }}>{durum.detay}</div>}
        </div>
      )}

      {aylar.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>Mutabakat raporu:</span>
          {aylar.map(a => (
            <span key={a.ay} title={a.tam ? `kapanış ${fmtNum(a.kapanis)} ₽ · ${a.satir} satır` : 'kapanış satırı yok — eksik'}
                  style={{ padding: '2px 8px', borderRadius: 10,
                           background: a.tam ? '#f0fdf4' : '#fef2f2', color: a.tam ? '#065f46' : '#991b1b' }}>
              {a.ay} · {a.tam ? a.kaynak : 'eksik'}
            </span>
          ))}
          <span style={{ color: '#9ca3af' }}>— ay geç gelirse panelden o ayı tek başına indirip buraya yükleyin</span>
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 12 }}>
        <button onClick={() => setAcik(a => !a)}
                style={{ background: 'none', border: 'none', color: '#2563eb',
                         cursor: 'pointer', padding: 0, fontSize: 12 }}>
          {acik ? '▾' : '▸'} yüklü belgeler ({liste.length})
        </button>
        {acik && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead><tr style={{ color: '#6b7280', textAlign: 'left' }}>
              <th style={th}>Belge</th><th style={th}>Tip</th><th style={th}>Tarih</th>
              <th style={th}>Dönem</th><th style={{ ...th, textAlign: 'right' }}>Tutar $</th>
              <th style={{ ...th, textAlign: 'right' }}>Satır</th><th style={th}>Yüklendi</th>
            </tr></thead>
            <tbody>
              {liste.map(b => (
                <tr key={b.tip + b.belge_no} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={td}>{b.belge_no}</td>
                  <td style={td}>{b.tip}</td>
                  <td style={td}>{formatDate(b.belge_tarihi)}</td>
                  <td style={td}>{b.donem || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {b.toplam_usd == null ? '—' : fmtNum(b.toplam_usd)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{b.satir}</td>
                  <td style={{ ...td, color: '#9ca3af' }}>{b.yuklendi ? `${formatDate(b.yuklendi)} ${b.yuklendi.slice(11)}` : '—'}</td>
                </tr>
              ))}
              {!liste.length && <tr><td style={{ ...td, color: '#9ca3af' }} colSpan={7}>henüz belge yok</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Bir kovanın içindeki kalemler. Kaynak, özetin kullandığı kaynağın AYNISI
// (panel raporu varsa o) — iki kaynağı karıştırmak çift sayma olurdu.
function KovaDetay({ d, parasal }) {
  if (!d) return null;
  if (d === 'yukleniyor') return <div style={{ padding: 10, fontSize: 12, color: '#6b7280' }}>yükleniyor…</div>;
  if (d.hata) return <div style={{ padding: 10, fontSize: 12, color: '#991b1b' }}>okunamadı: {d.hata}</div>;
  if (!d.rows || !d.rows.length) return <div style={{ padding: 10, fontSize: 12, color: '#9ca3af' }}>kalem yok</div>;
  return (
    <div style={{ padding: '8px 10px 10px 26px' }}>
      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>
        kaynak: {d.kaynak === 'panel_raporu' ? 'panel tahakkuk raporu' : 'API (türetilmiş)'}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ color: '#6b7280', textAlign: 'left' }}>
          <th style={th}>Kalem</th><th style={th}>Grup</th>
          <th style={{ ...th, textAlign: 'center' }}>Satır</th>
          <th style={{ ...th, textAlign: 'right' }}>RUB</th>
          <th style={th}>Tarih aralığı</th>
        </tr></thead>
        <tbody>
          {d.rows.map((r, i) => (
            <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
              {/* Ozon kalem adlarını Rusça gönderiyor; Türkçesi üstte, aslı altta küçük punto */}
              <td style={td}>
                <div>{tipTr(r.tip) || r.tip || (r.type_id != null ? `tip ${r.type_id}` : '—')}</div>
                {tipTr(r.tip) && <div style={{ fontSize: 10, color: '#9ca3af' }}>{r.tip}</div>}
                {r.type_id != null && <div style={{ fontSize: 10, color: '#cbd5e1' }}>tip {r.type_id}</div>}
              </td>
              <td style={{ ...td, color: '#9ca3af' }}>
                {grupTr(r.grup) || r.grup || '—'}
                {grupTr(r.grup) && <div style={{ fontSize: 10, color: '#cbd5e1' }}>{r.grup}</div>}
              </td>
              <td style={{ ...td, textAlign: 'center', color: '#6b7280' }}>{r.satir}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                           color: Number(r.rub) < 0 ? '#b91c1c' : '#111' }}>{parasal(r.rub)}</td>
              <td style={{ ...td, color: '#9ca3af' }}>{r.ilk}{r.son && r.son !== r.ilk ? ` – ${r.son}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MutBelgeler({ ozet, parasal, onYuklendi }) {
  const k = ozet.kaynak;
  // Kova detayı: toplam "neyin toplamı" olduğunu göstermiyordu; imha gibi tek
  // satırlık kalemler içeride kayboluyordu. Satıra tıklayınca kalemler açılır.
  const [acikKova, setAcikKova] = useState(null);
  const [detay, setDetay]       = useState({});   // { KOVA: {rows, kaynak} | 'yukleniyor' | 'hata' }

  const kovaAc = (kova) => {
    if (acikKova === kova) { setAcikKova(null); return; }
    setAcikKova(kova);
    if (detay[kova]) return;
    setDetay(d => ({ ...d, [kova]: 'yukleniyor' }));
    apiFetch(`${MUT_API}/${ozet.donem}/kova/${encodeURIComponent(kova)}`)
      .then(r => r.json())
      .then(d => setDetay(x => ({ ...x, [kova]: d })))
      .catch(e => setDetay(x => ({ ...x, [kova]: { hata: String(e.message || e) } })));
  };
  // ≈ işareti: kur çevirimi içeren değer. Kesin olanlar (belge, native USD) işaretsiz.
  const yak = v => (v == null ? '—' : `≈ ${parasal(v, '$')}`);
  return (
    <div style={{ padding: 14 }}>
      <MutBelgeYukle onYuklendi={onYuklendi} />

      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
        Tahakkuk aralığı {formatDate(ozet.aralik.bas)} – {formatDate(ozet.aralik.bit)} ·
        kova kaynağı: <b>{ozet.kova_kaynagi === 'panel_raporu' ? 'panel tahakkuk raporu' : 'API (türetilmiş)'}</b>
      </div>

      {k && (
        <div style={{ padding: 10, borderRadius: 6, marginBottom: 14, fontSize: 13,
                      background: k.komisyon_guven === 'fiili' ? '#f0fdf4' : '#fffbeb',
                      color: k.komisyon_guven === 'fiili' ? '#065f46' : '#92400e' }}>
          <b>Komisyon kaynağı:</b> {k.komisyon_kaynak === 'panel_raporu' ? 'panel raporu (fiilî)' : 'API (türetilmiş)'}
          {' · '}{parasal(k.komisyon_rub)}
          {k.sapma_yuzde != null && k.komisyon_kaynak === 'panel_raporu' && (
            <> · API'ye göre <b>%{Number(k.sapma_yuzde).toFixed(2)}</b> daha yüksek</>
          )}
          {k.komisyon_guven === 'yaklasik_dusuk' && (
            <div style={{ marginTop: 4 }}>
              ⚠ Panel raporu yüklenmediği için komisyon <b>%2,6–19 arası eksik</b> olabilir.
              Akt sağlaması bu dönemde geçerli değildir.
            </div>
          )}
        </div>
      )}

      {/* ── KAPILAR — yalnızca ₽. Dolar sağlaması kapı DEĞİLDİR. ── */}
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
        Sağlama kapıları
        <Ipucu baslik="Sağlama kapısı nedir?" metin={ACIKLAMA.kapi} />
      </div>
      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>
        Her belge <b>doğduğu birimde</b> sağlanır. Bu tablo yalnızca ruble üzerinden
        PASS/FAIL üretir; dolar karşılaştırması aşağıda, bilgi amaçlıdır.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }}>
        <thead><tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
          <th style={{ ...th, textAlign: 'left' }}>Kapı</th>
          <th style={{ ...th, textAlign: 'right' }}>Model</th>
          <th style={{ ...th, textAlign: 'right' }}>Resmî belge</th>
          <th style={{ ...th, textAlign: 'right' }}>Fark</th>
          <th style={th}>Durum</th>
        </tr></thead>
        <tbody>
          {ozet.kapilar.map((x, i) => {
            const r = DURUM_ROZET[x.durum] || DURUM_ROZET.veri_yok;
            const bir = x.birim === 'adet' ? '' : (x.birim || '₽');
            return (
              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={td}>{x.ad}
                  {x.aciklama && <div style={{ fontSize: 11, color: '#9ca3af' }}>{x.aciklama}</div>}
                  {x.kalemler && x.kalemler.length > 0 && (
                    <table style={{ fontSize: 11, marginTop: 4, color: '#6b7280', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={{ textAlign: 'left', paddingRight: 10 }}>kalem</th>
                        <th style={{ textAlign: 'right', paddingRight: 10 }}>akt</th>
                        <th style={{ textAlign: 'right', paddingRight: 10 }}>+ puan</th>
                        <th style={{ textAlign: 'right', paddingRight: 10 }}>rapor</th>
                        <th style={{ textAlign: 'right' }}>fark</th>
                      </tr></thead>
                      <tbody>{x.kalemler.map(k => (
                        <tr key={k.kalem} style={{ color: k.durum === 'gecti' ? '#6b7280' : '#dc2626' }}>
                          <td style={{ paddingRight: 10 }}>{k.durum === 'gecti' ? '✓' : '✗'} {k.kalem}{k.kayar ? ' (kaymış)' : ''}{k.not ? ` — ${k.not}` : ''}</td>
                          <td style={{ textAlign: 'right', paddingRight: 10 }}>{parasal(k.akt_usd, '$')}</td>
                          <td style={{ textAlign: 'right', paddingRight: 10 }}>{parasal(k.puan_usd, '$')}</td>
                          <td style={{ textAlign: 'right', paddingRight: 10 }}>{parasal(k.rapor_usd, '$')}</td>
                          <td style={{ textAlign: 'right' }}>{parasal(k.fark, '$')} <span style={{ color: '#9ca3af' }}>±{k.tolerans}</span></td>
                        </tr>))}</tbody>
                    </table>
                  )}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {x.birim === 'adet' ? (x.model ?? '—') : parasal(x.model, bir)}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {x.birim === 'adet' ? (x.resmi ?? '—') : parasal(x.resmi, bir)}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                             color: r.renk, fontWeight: 700 }}>
                  {x.birim === 'adet' ? (x.fark ?? '—') : parasal(x.fark, bir)}</td>
                <td style={{ ...td, color: r.renk, fontWeight: 700 }}>{r.im} {r.ad}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── BİLGİ — USD karşılaştırması, PASS/FAIL üretmez ── */}
      {ozet.usd_bilgi && ozet.usd_bilgi.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
            Bilgi · ödeme belgesi (USD)
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>
            Ozon her satırı kendi kuruyla çevirip topluyor; biz topladıktan sonra
            çeviriyoruz. Bu yüzden <b>≈</b> işaretli satırlar kuruşuna tutmaz — bu normaldir
            ve <b>kapı değildir</b>.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 14 }}>
            <thead><tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ ...th, textAlign: 'left' }}>Belge satırı</th>
              <th style={{ ...th, textAlign: 'right' }}>Belge $</th>
              <th style={{ ...th, textAlign: 'right' }}>Bizim $</th>
              <th style={{ ...th, textAlign: 'right' }}>Fark</th>
              <th style={{ ...th, textAlign: 'left' }}>Kaynak</th>
            </tr></thead>
            <tbody>
              {ozet.usd_bilgi.map((u, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={td}>{u.aciklama}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700,
                               fontVariantNumeric: 'tabular-nums' }}>{parasal(u.belge_usd, '$')}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#6b7280',
                               fontVariantNumeric: 'tabular-nums' }}>
                    {u.bizim_usd == null ? '—' : (u.cevrilmis ? yak(u.bizim_usd) : parasal(u.bizim_usd, '$'))}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#9ca3af',
                               fontVariantNumeric: 'tabular-nums' }}>
                    {u.fark == null ? '—' : parasal(u.fark, '$')}</td>
                  <td style={{ ...td, fontSize: 11, color: '#9ca3af' }}>{u.kaynak || '—'}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700, background: '#f0fdf4' }}>
                <td style={td}>ÖDENEN (belge toplamı)</td>
                <td style={{ ...td, textAlign: 'right', color: '#065f46',
                             fontVariantNumeric: 'tabular-nums' }}>
                  {parasal(ozet.odeme_belgesi_toplam_usd, '$')}</td>
                <td style={td}></td><td style={td}></td><td style={td}></td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* ── DÖNEM KÂRI — sipariş kârı ile gerçek kâr arasındaki köprü ── */}
      {ozet.donem_kari && ozet.donem_kari.siparis_kar_usd != null && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
            Dönem kârı
            <Ipucu baslik="Bu tablo neyi gösteriyor?" metin={ACIKLAMA.dk_ozon} />
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>
            Sipariş satırlarındaki kâr, Ozon'a ödediğinizin tamamı değildir. Bu tablo aradaki köprüyü kurar.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={td}>
                  Sipariş bazlı brüt kâr
                  <Ipucu baslik="Sipariş bazlı brüt kâr" metin={ACIKLAMA.dk_siparis} />
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    net {parasal(ozet.donem_kari.kapsanan_net_usd, '$')} − ürün maliyeti{' '}
                    {parasal(ozet.donem_kari.maliyet_usd, '$')}
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                  {parasal(ozet.donem_kari.siparis_kar_usd, '$')}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={td}>
                  Dönem giderleri (siparişe bağlanamayan)
                  <Ipucu baslik="Dönem giderleri" metin={ACIKLAMA.dk_donem} />
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    {(ozet.donem_kari.gonderisiz_kalemler || []).map(x =>
                      `${x.ad} ${parasal(x.rub)}`).join(' · ') || 'yok'}
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#b91c1c' }}>
                  {parasal(ozet.donem_kari.gonderisiz_usd, '$')}</td>
              </tr>
              {ozet.donem_kari.maliyetsiz_net_usd != null
                && Math.abs(ozet.donem_kari.maliyetsiz_net_usd) >= 0.01 && (
                <tr style={{ borderBottom: '1px solid #f0f0f0', color: '#6b7280' }}>
                  <td style={td}>
                    Maliyeti bilinmeyen gönderiler
                    <Ipucu baslik="Maliyeti bilinmeyen gönderiler" metin={ACIKLAMA.dk_maliyetsiz} />
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>
                      kâr hesabına girmez — bu gönderilerin ürün maliyeti girilmemiş
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {parasal(ozet.donem_kari.maliyetsiz_net_usd, '$')}</td>
                </tr>
              )}
              <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700 }}>
                <td style={td}>
                  Ozon sonrası kâr
                  <Ipucu baslik="Ozon sonrası kâr" metin={ACIKLAMA.dk_ozon} />
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                             color: Number(ozet.donem_kari.ozon_sonrasi_kar_usd) < 0 ? '#dc2626' : '#065f46' }}>
                  {parasal(ozet.donem_kari.ozon_sonrasi_kar_usd, '$')}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
            Maliyet kapsamı: {ozet.donem_kari.maliyetli_gonderi}/{ozet.donem_kari.gonderi} gönderi
            (%{fmtNum(ozet.donem_kari.kapsam_oran || 0, 1)})
            <Ipucu baslik="Kapsam ne demek?" metin={ACIKLAMA.dk_kapsam} />
            {' · '}Türkiye tarafı giderler (nakliye, gümrük, banka, genel gider) bu tabloda <b>yoktur</b>.
          </div>
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
        Kova kırılımı
        <Ipucu baslik="Kova nedir?" metin={"Ozon'un gönderdiği her kalem, anlamına göre bir gruba (kovaya) konur.\nBöylece hangi paranın nereye gittiği görünür ve resmî belgelerle karşılaştırılabilir.\nSatıra tıklayınca o kovanın içindeki kalemler açılır."} />
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
          <th style={{ ...th, textAlign: 'left' }}>Kova</th>
          <th style={th}>Satır</th>
          <th style={{ ...th, textAlign: 'right' }}>RUB</th>
          <th style={{ ...th, textAlign: 'right' }}>USD ≈</th>
        </tr></thead>
        <tbody>
          {ozet.kovalar.map(x => (
            <React.Fragment key={x.kova}>
            <tr onClick={() => kovaAc(x.kova)}
                title="kalemleri göster"
                style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                  background: x.kova === 'EŞLEŞMEYEN' ? '#fef2f2'
                            : acikKova === x.kova ? '#f9fafb' : undefined }}>
              <td style={{ ...td, fontWeight: x.kova === 'EŞLEŞMEYEN' ? 700 : 400,
                           color: x.kova === 'EŞLEŞMEYEN' ? '#991b1b' : '#111' }}>
                <span style={{ color: '#9ca3af', marginRight: 4 }}>{acikKova === x.kova ? '▾' : '▸'}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#9ca3af',
                               marginRight: 6 }}>{x.kova}</span>
                {(KOVA_BILGI[x.kova] || [])[0] || ''}
                <Ipucu baslik={(KOVA_BILGI[x.kova] || [])[0] || x.kova} metin={ACIKLAMA[x.kova]} />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                  {(KOVA_BILGI[x.kova] || [])[1] || ''}</div>
              </td>
              <td style={{ ...td, textAlign: 'center', color: '#6b7280' }}>{x.satir}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                           color: Number(x.rub) < 0 ? '#b91c1c' : '#111' }}>{parasal(x.rub)}</td>
              <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                           color: '#9ca3af' }}>{yak(x.usd)}</td>
            </tr>
            {acikKova === x.kova && (
              <tr><td colSpan={4} style={{ padding: 0, background: '#fbfbfb' }}>
                <KovaDetay d={detay[x.kova]} parasal={parasal} />
              </td></tr>
            )}
            </React.Fragment>
          ))}
          <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700 }}>
            <td style={td}>TOPLAM</td><td style={td}></td>
            <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{parasal(ozet.toplam_rub)}</td>
            <td style={{ ...td, textAlign: 'right', color: '#9ca3af',
                         fontVariantNumeric: 'tabular-nums' }}>{yak(ozet.toplam_usd)}</td>
          </tr>
          {ozet.hasilat_usd_belge != null && (
            <tr style={{ background: '#f0fdf4' }}>
              <td style={td}>Hasılat — <b>belgeden</b> (kesin, çevrim yok)</td><td style={td}></td>
              <td style={td}></td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#065f46',
                           fontVariantNumeric: 'tabular-nums' }}>{parasal(ozet.hasilat_usd_belge, '$')}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280', lineHeight: 1.7 }}>
        <div>• <b>≈</b> işareti kur çevirimi içerdiğini gösterir: ruble × round(1/CBR(<b>sipariş
          tarihi</b>), 6). Kur bazı sipariş tarihidir, tahakkuk tarihi değil (ölçüldü).</div>
        <div>• Hasılatın kesin USD'si yeşil satırdadır — belgeden gelir, çevrim yapılmaz.</div>
      </div>
    </div>
  );
}

function MutSiparisler({ donem, parasal }) {
  const [rows, setRows] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [toplamlar, setToplamlar] = useState(null);
  const [bas, setBas] = useState('');
  const [bit, setBit] = useState('');
  const [ara, setAra] = useState('');
  const [page, setPage] = useState(1);
  const [boy, setBoy] = useState(100);
  const [yukleniyor, setYukleniyor] = useState(true);

  const qs = () => {
    const p = new URLSearchParams({ page: String(page), page_size: String(boy) });
    if (bas) p.set('bas', bas); if (bit) p.set('bit', bit); if (ara) p.set('ara', ara);
    return p.toString();
  };

  useEffect(() => {
    if (!donem) return;
    setYukleniyor(true);
    apiFetch(`${MUT_API}/${donem}/siparisler?${qs()}`).then(r => r.json()).then(d => {
      setRows(d.rows || []); setToplam(d.toplam || 0);
      setToplamlar(d.toplamlar || null); setYukleniyor(false);
    }).catch(() => setYukleniyor(false));
  }, [donem, page, boy, bas, bit, ara]);

  useEffect(() => { setPage(1); }, [donem, bas, bit, ara, boy]);

  // CSV: window.open Authorization taşımaz — token'lı fetch + blob (bkz. §19/Excel dersi)
  const csvIndir = async () => {
    const r = await apiFetch(`${MUT_API}/${donem}/csv?${qs()}`);
    if (!r.ok) return;
    const b = await r.blob(); const u = URL.createObjectURL(b);
    const a = document.createElement('a'); a.href = u; a.download = `mutabakat-${donem}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u);
  };

  const kovaAdlari = [...new Set(rows.flatMap(r => Object.keys(r.kovalar || {})))].sort();
  const sayfaSayisi = Math.max(1, Math.ceil(toplam / boy));

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: '#fafafa',
                    borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Gönderi ara…" value={ara} onChange={e => setAra(e.target.value)}
               style={{ ...styles.input, width: 180 }} />
        <span style={{ fontSize: 12, color: '#6b7280' }}>Tahakkuk:</span>
        <input type="date" value={bas} onChange={e => setBas(e.target.value)} style={styles.input} />
        <span style={{ color: '#9ca3af' }}>–</span>
        <input type="date" value={bit} onChange={e => setBit(e.target.value)} style={styles.input} />
        {(ara || bas || bit) && (
          <button onClick={() => { setAra(''); setBas(''); setBit(''); }} style={styles.btnSec}>Temizle</button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{toplam.toLocaleString('tr-TR')} gönderi</span>
          <button onClick={csvIndir} style={{ ...styles.btnPri, background: '#16a34a' }}>⭳ CSV İndir</button>
        </div>
      </div>

      {yukleniyor ? <div style={styles.loading}>Yükleniyor…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ ...th, textAlign: 'left' }}>Gönderi No</th>
              <th style={th}>Sipariş</th><th style={th}>Sevk</th><th style={th}>Kargo</th>
              <th style={th}>Teslim (plan)</th><th style={th}>Teslim (gerçek)</th>
              <th style={th}>Tahakkuk</th><th style={th}>Durum</th>
              {kovaAdlari.map(k => (
                <th key={k} style={{ ...th, textAlign: 'right', whiteSpace: 'nowrap' }}
                    title={(KOVA_BILGI[k] || [])[1] || k}>
                  {(KOVA_BILGI[k] || [])[0] || k}
                  <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400,
                                fontFamily: 'monospace' }}>{k}</div>
                </th>
              ))}
              <th style={{ ...th, textAlign: 'right' }}>
                Hasılat $<Ipucu baslik="Hasılat $ (belge)" metin={ACIKLAMA.hasilat_belge} />
                <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>belge</div></th>
              <th style={{ ...th, textAlign: 'right' }}>
                Ozon sonrası net ₽<Ipucu baslik="Ozon sonrası net ₽" metin={ACIKLAMA.net_rub} /></th>
              <th style={{ ...th, textAlign: 'right' }}>
                Ozon sonrası net $<Ipucu baslik="Ozon sonrası net $" metin={ACIKLAMA.net_usd} /></th>
              <th style={{ ...th, textAlign: 'right' }}>
                Maliyet $<Ipucu baslik="Maliyet $" metin={ACIKLAMA.maliyet_usd} />
                <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>alış</div></th>
              <th style={{ ...th, textAlign: 'right' }}>
                Brüt kâr $<Ipucu baslik="Brüt kâr $" metin={ACIKLAMA.kar_usd} /></th>
              <th style={{ ...th, textAlign: 'right' }}>
                Puan katkısı $<Ipucu baslik="Puan katkısı $" metin={ACIKLAMA.puan_usd} />
                <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>Ozon puanı</div></th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.gonderi} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.gonderi}</td>
                  <td style={td}>{formatDate(r.siparis_tarihi)}</td>
                  <td style={td}>{formatDate(r.sevk_tarihi)}</td>
                  <td style={td}>{formatDate(r.kargo_tarihi)}</td>
                  <td style={td}>{formatDate(r.teslim_plan)}</td>
                  <td style={td}>{formatDate(r.teslim_gercek)}</td>
                  <td style={td}>{formatDate(r.tahakkuk_tarihi)}</td>
                  <td style={td}><Badge status={r.status} /></td>
                  {kovaAdlari.map(k => (
                    <td key={k} style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                          color: (r.kovalar && Number(r.kovalar[k]) < 0) ? '#b91c1c' : '#111' }}>
                      {r.kovalar && r.kovalar[k] != null ? parasal(r.kovalar[k]) : '—'}
                    </td>
                  ))}
                  <td style={{ ...td, textAlign: 'right', color: '#065f46',
                               fontVariantNumeric: 'tabular-nums' }}>
                    {r.hasilat_usd == null ? '—' : parasal(r.hasilat_usd, '$')}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700,
                               fontVariantNumeric: 'tabular-nums' }}>{parasal(r.net_rub)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#065f46',
                               fontVariantNumeric: 'tabular-nums' }}>{parasal(r.net_usd, '$')}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#6b7280',
                               fontVariantNumeric: 'tabular-nums' }}
                      title={r.maliyet_eksik ? 'Kalemlerden birinin maliyeti girilmemiş' : undefined}>
                    {r.maliyet_usd == null ? '—' : parasal(r.maliyet_usd, '$')}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700,
                               color: r.kar_usd == null ? '#9ca3af' : (Number(r.kar_usd) < 0 ? '#dc2626' : '#065f46'),
                               fontVariantNumeric: 'tabular-nums' }}
                      title={r.kar_usd == null ? 'Maliyet girilmemiş — kâr hesaplanmadı' : undefined}>
                    {r.kar_usd == null ? '—' : parasal(r.kar_usd, '$')}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                               color: Number(r.puan_usd || 0) > 0.005 ? '#92400e' : '#9ca3af' }}
                      title={Number(r.puan_usd || 0) > 0.005
                        ? 'Bu siparişin bir kısmı Ozon puanıyla ödendi — kâr iyimser görünür'
                        : undefined}>
                    {r.puan_usd == null || Number(r.puan_usd) < 0.005 ? '—' : parasal(r.puan_usd, '$')}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={14 + kovaAdlari.length}
                style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 24 }}>Kayıt yok</td></tr>}
              {toplamlar && (
                <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 700, background: '#f9fafb' }}>
                  <td style={td} colSpan={8 + kovaAdlari.length}>
                    TOPLAM — {toplam.toLocaleString('tr-TR')} gönderi
                    <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}>
                      {' '}(görünen sayfa değil, filtreye uyan tüm kayıtlar)</span>
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: '#065f46',
                               fontVariantNumeric: 'tabular-nums' }}>{parasal(toplamlar.hasilat_usd, '$')}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {parasal(toplamlar.net_rub)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {parasal(toplamlar.net_usd, '$')}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#6b7280' }}>
                    {parasal(toplamlar.maliyet_usd, '$')}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                               color: Number(toplamlar.kar_usd) < 0 ? '#dc2626' : '#065f46' }}>
                    {parasal(toplamlar.kar_usd, '$')}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#92400e' }}>
                    {parasal(toplamlar.puan_usd, '$')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Kâr kapsamı — maliyeti girilmemiş gönderilerde kâr BOŞ kalır, sıfır değil */}
      {toplamlar && (
        <div style={{ margin: '10px 14px', padding: 10, borderRadius: 6, fontSize: 13,
                      background: '#f9fafb', color: '#374151' }}>
          <b>Kâr kapsamı:</b> {toplamlar.maliyetli_gonderi?.toLocaleString('tr-TR') || 0} / {toplam.toLocaleString('tr-TR')} gönderi
          {' '}(%{fmtNum(toplamlar.maliyet_kapsam_oran || 0, 1)}) — maliyeti girilmemiş gönderide kâr boş bırakıldı.
          {toplamlar.maliyetli_gonderi > 0 && (
            <>
              {' · '}<b>kapsamdaki net</b> {parasal(toplamlar.kar_net_usd, '$')}
              {' · '}<b>maliyet</b> {parasal(toplamlar.maliyet_usd, '$')}
              {' · '}<b>brüt kâr</b> {parasal(toplamlar.kar_usd, '$')}
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                maliyetin kur kaynağı: elle girilen {parasal(toplamlar.maliyet_manuel_usd, '$')} ·
                TCMB otomatik {parasal(toplamlar.maliyet_tcmb_usd, '$')}
                {toplamlar.puan_usd > 0.005 && (
                  <> · <b>{toplamlar.puanli_gonderi}</b> gönderide Ozon puanı kullanılmış,
                    toplam {parasal(toplamlar.puan_usd, '$')} — o siparişlerin kârı iyimser görünür
                    <Ipucu baslik="Puan katkısı" metin={ACIKLAMA.puan_usd} /></>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Belge karşılaştırması — kullanıcının asıl kontrolü */}
      {toplamlar && toplamlar.belge_hasilat_usd != null && (
        <div style={{ margin: '10px 14px', padding: 10, borderRadius: 6, fontSize: 13,
                      background: (toplamlar.filtreli || toplamlar.hasilat_kapsam === false) ? '#f9fafb'
                        : (Math.abs(toplamlar.hasilat_fark) < 0.02 ? '#f0fdf4' : '#fef2f2'),
                      color: (toplamlar.filtreli || toplamlar.hasilat_kapsam === false) ? '#6b7280'
                        : (Math.abs(toplamlar.hasilat_fark) < 0.02 ? '#065f46' : '#991b1b') }}>
          {toplamlar.hasilat_kapsam === false ? (
            <>— <b>Kapsam dışı:</b> {toplamlar.hasilat_kapsam_notu}.
              Belgedeki hasılat: {parasal(toplamlar.belge_hasilat_usd, '$')} ·
              hasılatı bilinen gönderi: {fmtNum(toplam - (toplamlar.hasilatsiz_gonderi || 0), 0)} / {fmtNum(toplam, 0)}</>
          ) : toplamlar.filtreli ? (
            <>ⓘ <b>Filtre açık</b> — belge karşılaştırması yalnızca filtresiz yapılabilir.
              Belgedeki hasılat: {parasal(toplamlar.belge_hasilat_usd, '$')} ·
              filtreye uyan: {parasal(toplamlar.hasilat_usd, '$')}</>
          ) : (
            <>
              {Math.abs(toplamlar.hasilat_fark) < 0.02 ? '✓' : '✗'}{' '}
              <b>Siparişlerin hasılat toplamı</b> {parasal(toplamlar.hasilat_belge_tanimi_usd ?? toplamlar.hasilat_usd, '$')}
              {toplamlar.hasilat_belge_tanimi_usd != null
                && Math.abs(toplamlar.hasilat_belge_tanimi_usd - toplamlar.hasilat_usd) >= 0.01 && (
                <span style={{ fontWeight: 400 }}> (belgenin tanımıyla: komisyon düşülmüş net · brüt {parasal(toplamlar.hasilat_usd, '$')})</span>
              )}
              {' · '}<b>ödeme belgesindeki hasılat satırı</b> {parasal(toplamlar.belge_hasilat_usd, '$')}
              {' · fark '}<b>{parasal(toplamlar.hasilat_fark, '$')}</b>
              {toplamlar.hasilatsiz_gonderi > 0 && (
                <span style={{ fontWeight: 400 }}> · {fmtNum(toplamlar.hasilatsiz_gonderi, 0)} gönderinin hasılatı raporda yok</span>
              )}
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Sayfa başı:</span>
        {[50, 100, 200, 500].map(s => (
          <button key={s} onClick={() => setBoy(s)} style={{ ...styles.btnSec,
            background: boy === s ? '#2563eb' : 'white', color: boy === s ? 'white' : '#374151' }}>{s}</button>
        ))}
        <span style={{ width: 12 }} />
        <button onClick={() => setPage(1)} disabled={page === 1} style={styles.btnSec}>«</button>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={styles.btnSec}>←</button>
        <span style={{ fontSize: 13, color: '#555' }}>{page} / {sayfaSayisi}</span>
        <button onClick={() => setPage(p => Math.min(sayfaSayisi, p + 1))} disabled={page >= sayfaSayisi} style={styles.btnSec}>→</button>
        <button onClick={() => setPage(sayfaSayisi)} disabled={page >= sayfaSayisi} style={styles.btnSec}>»</button>
      </div>

      <div style={{ padding: '0 14px 16px', fontSize: 12, color: '#b45309' }}>
        ⚠ <b>"Ozon sonrası net"</b> kâr DEĞİLDİR. Ürün alış maliyeti, Türkiye içi lojistik
        ve gümrük hiçbir Ozon kaynağında yoktur (§19).
      </div>
    </div>
  );
}

export default function OzonFinance() {
  const [tab, setTab] = useState('payouts');
  const [selectedPayoutId, setSelectedPayoutId] = useState(null);
  const [payouts, setPayouts] = useState([]);

  useEffect(() => {
    apiFetch(`${API}/payouts?year=all`).then(r => r.json()).then(d => setPayouts(Array.isArray(d) ? d : d.payouts || []));
  }, []);

  const goToOrders = (payoutId) => {
    setSelectedPayoutId(payoutId);
    setTab('orders');
  };

  // Siparis satirindaki Donem hucresi -> Odemeler sekmesinde o donemi ac
  const goToPayout = (payoutId) => {
    setSelectedPayoutId(payoutId);
    setTab('payouts');
  };

  return (
    <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
      {/* Başlık */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Ozon Finans</h1>
        <SyncButton />
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
        {[['payouts','Ödemeler'],['orders','Siparişler'],['mutabakat','Mutabakat'],['maliyet','Maliyet']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '10px 24px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14,
            fontWeight: tab === k ? 700 : 400,
            borderBottom: tab === k ? '2px solid #2563eb' : '2px solid transparent',
            color: tab === k ? '#2563eb' : '#555'
          }}>{l}</button>
        ))}
      </div>

      {tab === 'payouts' && <PayoutsTab onSelectPayout={goToOrders} focusPayoutId={selectedPayoutId} />}
      {tab === 'orders' && <OrdersTab initialPayoutId={selectedPayoutId} payouts={payouts} onGoToPayout={goToPayout} />}
      {tab === 'mutabakat' && <MutabakatTab />}
      {tab === 'maliyet' && <MaliyetTab />}
    </div>
  );
}

// ── Stil sabitleri ─────────────────────────────────────────────────────────────
const th = { padding: '7px 10px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#555', whiteSpace: 'nowrap' };
const td = { padding: '6px 10px', verticalAlign: 'middle' };
const styles = {
  loading: { padding: 40, textAlign: 'center', color: '#888' },
  input:   { padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, background: 'white' },
  btnPri:  { padding: '6px 14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  btnSec:  { padding: '6px 12px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer', fontSize: 12 },
};
